import { useTranslation } from 'react-i18next';
import { getLocale } from '../../i18n';
import LocalizedNumberInput from '../../i18n/LocalizedNumberInput';
import { trDisplay } from '../../i18n';
import { SWR_MODEL_PORTFOLIOS as MODEL_PORTFOLIOS, getSWRPortfolioById } from '../../lib/simulation/swrPortfolioCalibration';
import { resolveSWRCashMode, describeSWRCashModel, SWR_CASH_DEFAULTS } from '../../lib/simulation/swrCashModel';
import React, { useState, useCallback, useEffect, useMemo, useRef, useId } from 'react';
import SWRHistoricalPortfolio from './SWRHistoricalPortfolio';
import SWRInitialGains from './SWRInitialGains.jsx';
import SWRWithdrawalStrategySelector from './SWRWithdrawalStrategySelector';
import { SWRConfigurationLevelSelector } from './SWRConfigurationPanel';
import { getSWRConfigurationCapabilities, resolveSWRConfigurationLevel } from './swrConfigurationLevels';
import { normalizeSWRWithdrawalStrategy, getSWRWithdrawalStrategyCapabilities, getSWRWithdrawalStrategyDefinition, SWR_WITHDRAWAL_STRATEGY_VERSION } from '../../lib/simulation/swrWithdrawalStrategies';
import { hasAssetInitialBases, portfolioGainFromBases } from './swrInitialTaxInputs';
import { createSWRFlowPortfolio, prepareSWRHistoricalPortfolio, getSWRCustomDraftConfig } from '../../lib/simulation/swrHistoricalPortfolio';
import { isSWRFlowTaxPortfolio, isSWRDebtPortfolio } from '../../lib/simulation/swrTaxModel';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { normalizeSWRContributions, SWR_MAX_CONTRIBUTIONS } from '../../lib/simulation/swrContributions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { SWRHelp } from './SWRHelp';
import { Play, Loader2, Wallet, ShieldCheck, CalendarDays, PiggyBank, GraduationCap, Clock, PieChart, Landmark, SlidersHorizontal } from 'lucide-react';
import { formatInputDisplay, formatPercentage } from '../../utils/formatters';
import {
  SWR_MAIN_DEFAULT_SIMULATIONS,
  SWR_MAIN_MAX_SIMULATIONS,
  SWR_MAIN_MIN_SIMULATIONS,
} from '../../lib/swrSimulationModes';
import {
  SWR_MACRO_MODE_FIXED,
  SWR_MACRO_MODE_STOCHASTIC_EURO_V1,
  SWR_MACRO_MODE_HISTORICAL_JOINT,
} from '../../lib/simulation/swrMacroModel';

const FormattedInput = LocalizedNumberInput;
const STRATEGY_OPTION_CARD_CLASS = 'rounded-lg border border-blue-100 bg-blue-50/60 p-3 sm:p-4 space-y-3';

const FormField = ({ label, help, error, children }) => {
  useTranslation();
  const generatedId = useId();
  const items = React.Children.toArray(children);
  const control = items.find(child => React.isValidElement(child)
    && (child.type === FormattedInput || child.type === Input || ['input', 'select', 'textarea'].includes(child.type)));
  const inputId = control?.props.id || generatedId;
  const errorId = `${inputId}-error`;
  return <div className="space-y-2" data-swr-field-error={error ? true : undefined}>
    <div className="flex items-center gap-2">
      <Label htmlFor={inputId} className="text-sm font-medium text-slate-700">{trDisplay(label)}</Label>
      <SWRHelp topic={help} />
    </div>
    {items.map(child => child === control ? React.cloneElement(child, {
      id: inputId,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? [child.props['aria-describedby'], errorId].filter(Boolean).join(' ') : child.props['aria-describedby'],
    }) : child)}
    {error && <p id={errorId} className="text-sm text-red-500">{trDisplay(error)}</p>}
  </div>;
};

const MAX_WITHDRAWAL_PERIODS = 4;
const DEFAULT_REAL_WITHDRAWAL_YEAR0 = 24000;
const DEFAULT_MONTHLY_WITHDRAWAL = DEFAULT_REAL_WITHDRAWAL_YEAR0 / 12;
const CASH_BUFFER_MINIMUM_MONTHS = 2;
const PLANNING_MODE_NOW = 'now';
const PLANNING_MODE_FUTURE = 'future';
const MAX_FUTURE_START_YEARS = 100;
const PORTFOLIO_PRESET_CUSTOM = 'custom';
const SWR_UI_MODE_STANDARD = 'standard';
const SWR_STANDARD_SIMULATIONS = 5000;
const SWR_STANDARD_CUSTOM_TAX_RATE_PCT = 26;
const SWR_STANDARD_INITIAL_GAIN_FRACTION_PCT = 0;
const SWR_RISK_CAPITAL_DEPLETION = 'capital-depletion';
const SWR_RISK_MINIMUM_INCOME = 'minimum-income-shortfall';

const createDefaultFormData = () => ({
  valuationDate: new Date().toISOString().slice(0, 10),
  planningMode: PLANNING_MODE_NOW,
  yearsUntilStart: 10,
  portfolioPreset: PORTFOLIO_PRESET_CUSTOM,
  historicalPortfolio: createSWRFlowPortfolio([], { enabled: false }),
  legacySavedPresetTaxRatePct: null,
  customReturnParameterUncertaintyEnabled: false,
  initialCapital: 1000000,
  capitalContributionsEnabled: false,
  capitalContributions: [],
  expectedReturnPct: 6,
  annualVolatilityPct: 10,
  maxSingleYearDrawdownPct: '',
  inflationRatePct: 2.8,
  macroMode: SWR_MACRO_MODE_FIXED,
  taxRatePct: 26,
  initialGainFractionPct: 50,
  annualPortfolioCostRatePct: 0,
  annualWealthTaxRatePct: 0.2,
  minimumIncomeCoveragePct: 100,
  safeWithdrawalRiskMetric: SWR_RISK_CAPITAL_DEPLETION,
  cashReturnMode: 'money-market-lagged',
  cashAnnualReturnPct: SWR_CASH_DEFAULTS.cashAnnualReturn * 100,
  cashLongRunReturnPct: SWR_CASH_DEFAULTS.cashLongRunReturn * 100,
  cashRateHalfLifeMonths: SWR_CASH_DEFAULTS.cashRateHalfLifeMonths,
  cashInflationPassThroughPct: SWR_CASH_DEFAULTS.cashInflationPassThrough * 100,
  cashTaxRatePct: 26,
  cashBufferFunding: 'existing-cash',
  yearsToSimulate: 40,
  numberOfSimulations: SWR_MAIN_DEFAULT_SIMULATIONS,
  withdrawalPeriods: [{ monthlyWithdrawal: DEFAULT_MONTHLY_WITHDRAWAL, years: 40 }],
  guardrailsEnabled: false,
  guardrailMinEnabled: true,
  guardrailMaxEnabled: true,
  guardrailMinPct: 10,
  guardrailMaxPct: 10,
  dynamicCashBufferEnabled: false,
  withdrawalStrategy: normalizeSWRWithdrawalStrategy(),
  strategyCustomizationEnabled: false,
  withdrawalCustomizationEnabled: false,
  minimumMonthlyRealIncome: '',
  cashBufferMonths: 12,
  randomSeed: '',
  restoredAutomaticSeed: null,
});

const percentFromDecimal = (value, fallback) => (
  value === null || value === undefined ? fallback : safeNumber(value, fallback / 100) * 100
);

const monthlyWithdrawalFromSavedPeriod = (period, preSimulationInflationFactor = 1, valuesAreToday = false) => {
  if (period?.todayMonthlyWithdrawal !== null && period?.todayMonthlyWithdrawal !== undefined) {
    return safeNumber(period.todayMonthlyWithdrawal, DEFAULT_MONTHLY_WITHDRAWAL);
  }
  if (period?.todayAnnualWithdrawal !== null && period?.todayAnnualWithdrawal !== undefined) {
    return safeNumber(period.todayAnnualWithdrawal, DEFAULT_REAL_WITHDRAWAL_YEAR0) / 12;
  }
  if (period?.monthlyWithdrawal !== null && period?.monthlyWithdrawal !== undefined) {
    if (valuesAreToday) {
      return safeNumber(period.monthlyWithdrawal, DEFAULT_MONTHLY_WITHDRAWAL);
    }
    const factor = safeNumber(preSimulationInflationFactor, 1);
    return safeNumber(period.monthlyWithdrawal, DEFAULT_MONTHLY_WITHDRAWAL) / factor;
  }
  if (period?.annualWithdrawal !== null && period?.annualWithdrawal !== undefined) {
    if (valuesAreToday) {
      return safeNumber(period.annualWithdrawal, DEFAULT_REAL_WITHDRAWAL_YEAR0) / 12;
    }
    const factor = safeNumber(preSimulationInflationFactor, 1);
    return safeNumber(period.annualWithdrawal, DEFAULT_REAL_WITHDRAWAL_YEAR0) / factor / 12;
  }
  return DEFAULT_MONTHLY_WITHDRAWAL;
};

const buildFormDataFromScenarioParams = (params) => {
  const defaults = createDefaultFormData();
  if (!params) return defaults;

  const planningMode = params.planningMode === PLANNING_MODE_FUTURE || params.futurePlanningEnabled
    ? PLANNING_MODE_FUTURE
    : PLANNING_MODE_NOW;
  const yearsUntilStart = wholeYears(params.yearsUntilStart ?? (
    planningMode === PLANNING_MODE_FUTURE ? 0 : defaults.yearsUntilStart
  ));
  const preSimulationInflationFactor = planningMode === PLANNING_MODE_FUTURE
    ? safeNumber(params.preSimulationInflationFactor ?? Math.pow(
      1 + safeNumber(params.inflationRate, defaults.inflationRatePct / 100),
      yearsUntilStart
    ), 1)
    : 1;
  const hasTodayPeriods = Array.isArray(params.withdrawalPeriodsToday) && params.withdrawalPeriodsToday.length > 0;
  const savedPeriods = hasTodayPeriods
    ? params.withdrawalPeriodsToday
    : (Array.isArray(params.withdrawalPeriods) ? params.withdrawalPeriods : []);
  const withdrawalPeriods = savedPeriods.length > 0
    ? savedPeriods.slice(0, MAX_WITHDRAWAL_PERIODS).map(period => ({
      monthlyWithdrawal: monthlyWithdrawalFromSavedPeriod(period, preSimulationInflationFactor, hasTodayPeriods),
      years: wholeYears(period?.years) || defaults.yearsToSimulate,
    }))
    : [{
      monthlyWithdrawal: monthlyWithdrawalFromSavedPeriod({
        todayAnnualWithdrawal: params.realWithdrawalYear0Today,
        annualWithdrawal: params.realWithdrawalYear0,
      }, preSimulationInflationFactor),
      years: wholeYears(params.yearsToSimulate) || defaults.yearsToSimulate,
    }];
  const portfolioPreset = params.portfolioPreset || params.modelPortfolio?.id || defaults.portfolioPreset;
  const isSavedModelPortfolio = Boolean(
    params.modelPortfolio || (portfolioPreset && portfolioPreset !== PORTFOLIO_PRESET_CUSTOM)
  );
  const customReturnParameterUncertaintyEnabled = Boolean(
    params.customReturnParameterUncertaintyEnabled
    ?? (!isSavedModelPortfolio
      ? (params.returnParameterUncertaintyEnabled ?? params.returnParameterUncertainty?.enabled)
      : false)
    ?? defaults.customReturnParameterUncertaintyEnabled
  );

  const savedStrategy = normalizeSWRWithdrawalStrategy(params.withdrawalStrategy);
  const savedCapabilities = getSWRWithdrawalStrategyCapabilities(savedStrategy);
  const savedGuardrails = savedCapabilities.allowsGuardrails && Boolean(params.guardrailsEnabled || params.withdrawalPolicy === 'guardrails');
  const savedCashBuffer = savedCapabilities.allowsCashBuffer && Boolean(params.dynamicCashBufferEnabled);
  return {
    ...defaults,
    valuationDate: params.valuationDate || defaults.valuationDate,
    planningMode,
    yearsUntilStart,
    portfolioPreset,
    historicalPortfolio: params.historicalPortfolio || defaults.historicalPortfolio,
    legacySavedPresetTaxRatePct: isSavedModelPortfolio && !params.simpleTaxProfile ? percentFromDecimal(params.taxRate, defaults.taxRatePct) : null,
    customReturnParameterUncertaintyEnabled,
    initialCapital: safeNumber(params.initialCapital, defaults.initialCapital),
    capitalContributionsEnabled: (params.capitalContributions || []).length > 0,
    capitalContributions: (params.capitalContributions || []).map(event => ({
      year: Math.ceil(event.month / 12), month: (event.month - 1) % 12 + 1, amount: event.amount, label: event.label || '',
    })),
    expectedReturnPct: percentFromDecimal(params.expectedReturn, defaults.expectedReturnPct),
    annualVolatilityPct: percentFromDecimal(params.annualVolatility, defaults.annualVolatilityPct),
    maxSingleYearDrawdownPct: params.maxSingleYearDrawdown === null || params.maxSingleYearDrawdown === undefined
      ? ''
      : percentFromDecimal(params.maxSingleYearDrawdown, 0),
    inflationRatePct: percentFromDecimal(params.inflationRate, defaults.inflationRatePct),
    macroMode: params.macroMode === SWR_MACRO_MODE_STOCHASTIC_EURO_V1
      ? SWR_MACRO_MODE_STOCHASTIC_EURO_V1
      : SWR_MACRO_MODE_FIXED,
    taxRatePct: percentFromDecimal(params.taxRate, defaults.taxRatePct),
    initialGainFractionPct: percentFromDecimal(params.initialGainFraction, 0),
    annualPortfolioCostRatePct: percentFromDecimal(params.annualPortfolioCostRate, 0),
    annualWealthTaxRatePct: percentFromDecimal(params.annualWealthTaxRate, 0),
    minimumIncomeCoveragePct: percentFromDecimal(params.minimumIncomeCoverage, 100),
    safeWithdrawalRiskMetric: params.safeWithdrawalRiskMetric === SWR_RISK_MINIMUM_INCOME
      ? SWR_RISK_MINIMUM_INCOME
      : SWR_RISK_CAPITAL_DEPLETION,
    cashReturnMode: resolveSWRCashMode(params),
    cashAnnualReturnPct: percentFromDecimal(params.cashAnnualReturn, defaults.cashAnnualReturnPct),
    cashLongRunReturnPct: percentFromDecimal(params.cashLongRunReturn, defaults.cashLongRunReturnPct),
    cashRateHalfLifeMonths: params.cashRateHalfLifeMonths ?? defaults.cashRateHalfLifeMonths,
    cashInflationPassThroughPct: percentFromDecimal(params.cashInflationPassThrough, defaults.cashInflationPassThroughPct),
    cashTaxRatePct: percentFromDecimal(params.cashTaxRate, 26),
    cashBufferFunding: params.cashBufferFunding ?? 'existing-cash',
    yearsToSimulate: wholeYears(params.yearsToSimulate) || defaults.yearsToSimulate,
    numberOfSimulations: wholeYears(params.numberOfSimulations) || defaults.numberOfSimulations,
    withdrawalPeriods,
    withdrawalStrategy: savedStrategy,
    strategyCustomizationEnabled: params.withdrawalCustomization != null || savedGuardrails || savedCashBuffer,
    withdrawalCustomizationEnabled: params.withdrawalCustomization != null,
    minimumMonthlyRealIncome: params.withdrawalCustomization?.minimumMonthlyRealIncome ?? '',
    guardrailsEnabled: savedGuardrails,
    guardrailMinEnabled: params.guardrailMinEnabled !== false,
    guardrailMaxEnabled: params.guardrailMaxEnabled !== false,
    guardrailMinPct: percentFromDecimal(params.guardrailMinPct, defaults.guardrailMinPct),
    guardrailMaxPct: percentFromDecimal(params.guardrailMaxPct, defaults.guardrailMaxPct),
    dynamicCashBufferEnabled: savedCashBuffer,
    cashBufferMonths: wholeYears(params.cashBufferMonths) || defaults.cashBufferMonths,
    randomSeed: params.randomSeed ?? '',
    restoredAutomaticSeed: params.randomSeed === '' || params.randomSeed == null
      ? params.effectiveRandomSeed ?? null
      : null,
  };
};
const safeNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const annualFromMonthly = (monthlyValue) => safeNumber(monthlyValue) * 12;

const wholeYears = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return 0;
  return Math.round(numericValue);
};

const formatYears = (years) => `${years} ${years === 1 ? 'anno' : 'anni'}`;

const getFuturePlanningYears = (formData) => (
  formData.planningMode === PLANNING_MODE_FUTURE
    ? wholeYears(formData.yearsUntilStart)
    : 0
);

const getInflationFactor = (inflationRatePct, years) => {
  const rate = safeNumber(inflationRatePct) / 100;
  return Math.pow(1 + rate, Math.max(0, wholeYears(years)));
};

const isEmptyInput = (value) => value === '' || value === null || value === undefined;

const getRangeError = (field, value, formData = {}) => {
  if (isEmptyInput(value)) return null;

  const numericValue = Number(value);
  const integerValue = Number.isInteger(numericValue);

  switch (field) {
    case 'initialCapital':
      return numericValue > 0 ? null : 'Il capitale iniziale deve essere maggiore di 0.';
    case 'yearsUntilStart':
      return integerValue && numericValue >= 0 && numericValue <= MAX_FUTURE_START_YEARS
        ? null
        : `Gli anni fino all'inizio devono essere un numero intero tra 0 e ${MAX_FUTURE_START_YEARS}.`;
    case 'expectedReturnPct':
      return numericValue >= -50 && numericValue <= 50 ? null : 'Il rendimento deve essere tra -50% e +50%.';
    case 'annualVolatilityPct':
      return numericValue >= 0 && numericValue <= 100 ? null : 'La volatilità deve essere tra 0% e 100%.';
    case 'maxSingleYearDrawdownPct':
      return numericValue > 0 && numericValue <= 100
        ? null
        : 'Se specificato, deve essere tra 1 e 100 (es. 30 = max -30%).';
    case 'inflationRatePct':
      if (formData.macroMode === SWR_MACRO_MODE_STOCHASTIC_EURO_V1 && numericValue <= -100) {
        return 'Con inflazione stocastica il valore deve essere strettamente maggiore di -100%.';
      }
      return numericValue > -100 && numericValue <= 100 ? null : 'L’inflazione deve essere maggiore di -100% e non superiore a +100%.';
    case 'taxRatePct':
      return numericValue >= 0 && numericValue < 100 ? null : "L'aliquota fiscale deve essere tra 0% e meno del 100%.";
    case 'initialGainFractionPct':
      return numericValue >= 0 && numericValue <= 100 ? null : 'La quota di plusvalenza deve essere tra 0% e 100%.';
    case 'yearsToSimulate':
      return integerValue && numericValue >= 1 && numericValue <= 100
        ? null
        : 'Gli anni di simulazione devono essere un numero intero tra 1 e 100.';
    case 'numberOfSimulations':
      return integerValue && numericValue >= SWR_MAIN_MIN_SIMULATIONS && numericValue <= SWR_MAIN_MAX_SIMULATIONS
        ? null
        : `Il numero di simulazioni deve essere un numero intero tra ${SWR_MAIN_MIN_SIMULATIONS.toLocaleString(getLocale())} e ${SWR_MAIN_MAX_SIMULATIONS.toLocaleString(getLocale())}.`;
    case 'minimumMonthlyRealIncome':
      return Number.isFinite(numericValue) && numericValue >= 0
        ? null
        : 'Il reddito minimo mensile deve essere un importo maggiore o uguale a zero.';
    case 'guardrailMinPct':
      return numericValue >= 0 && numericValue <= 100 ? null : 'Il guardrail minimo deve essere tra 0% e 100%.';
    case 'guardrailMaxPct':
      return numericValue >= 0 && numericValue <= 100 ? null : 'Il guardrail massimo deve essere tra 0% e 100%.';
    case 'cashBufferMonths': {
      if (!integerValue || numericValue < 6 || numericValue > 36) {
        return 'Il buffer deve essere un numero intero tra 6 e 36 mesi.';
      }
      const firstPeriod = buildVisibleWithdrawalPeriods(formData).periods[0];
      const firstMonthlyWithdrawal = firstPeriod?.monthlyWithdrawal || DEFAULT_MONTHLY_WITHDRAWAL;
      const preSimulationInflationFactor = formData.planningMode === PLANNING_MODE_FUTURE
        ? getInflationFactor(formData.inflationRatePct, getFuturePlanningYears(formData))
        : 1;
      const targetBuffer = firstMonthlyWithdrawal * preSimulationInflationFactor * numericValue;
      if (formData.initialCapital > 0 && targetBuffer >= formData.initialCapital) {
        return formData.planningMode === PLANNING_MODE_FUTURE
          ? 'La riserva iniziale stimata a inizio simulazione deve essere inferiore al patrimonio futuro.'
          : 'La riserva iniziale deve essere inferiore al capitale iniziale.';
      }
      return null;
    }
    default:
      return null;
  }
};

const getFutureNominalInfo = (period, formData) => {
  const yearsUntilStart = getFuturePlanningYears(formData);
  if (yearsUntilStart <= 0) return null;

  const yearsFromTodayToPeriod = yearsUntilStart + Math.max(0, (period?.startYear || 1) - 1);
  const factor = getInflationFactor(formData.inflationRatePct, yearsFromTodayToPeriod);

  return {
    yearsFromTodayToPeriod,
    factor,
    monthlyWithdrawal: safeNumber(period?.monthlyWithdrawal) * factor,
    annualWithdrawal: safeNumber(period?.annualWithdrawal) * factor,
  };
};

const getPortfolioPresetById = getSWRPortfolioById;

const getPeriodTone = (index) => ([
  {
    classes: 'border-blue-200 bg-blue-50 text-blue-950',
    barClass: 'bg-blue-500',
    hintTone: 'blue',
  },
  {
    classes: 'border-emerald-200 bg-emerald-50 text-emerald-950',
    barClass: 'bg-emerald-500',
    hintTone: 'emerald',
  },
  {
    classes: 'border-amber-200 bg-amber-50 text-amber-950',
    barClass: 'bg-amber-500',
    hintTone: 'amber',
  },
  {
    classes: 'border-slate-200 bg-slate-50 text-slate-800',
    barClass: 'bg-slate-400',
    hintTone: 'slate',
  },
][index] || {
  classes: 'border-slate-200 bg-slate-50 text-slate-800',
  barClass: 'bg-slate-400',
  hintTone: 'slate',
});

const AnnualAmountHint = ({ monthlyValue, tone = 'blue' }) => {
  useTranslation();
  const toneClass = {
    blue: 'border-blue-100 bg-blue-50/70 text-blue-900',
    emerald: 'border-emerald-100 bg-emerald-50/70 text-emerald-900',
    amber: 'border-amber-100 bg-amber-50/70 text-amber-900',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone] || 'border-blue-100 bg-blue-50/70 text-blue-900';

  return (
    <div className={`rounded-md border px-3 py-1.5 text-xs ${toneClass}`}>{trDisplay("Annuo: ")}<strong>{trDisplay(formatInputDisplay(annualFromMonthly(monthlyValue), { decimals: 0 }))} €</strong>
    </div>
  );
};

const FutureNominalHint = ({ period, formData, tone = 'blue' }) => {
  useTranslation();
  const futureInfo = getFutureNominalInfo(period, formData);
  if (!futureInfo) return null;

  const toneClass = {
    blue: 'border-indigo-100 bg-indigo-50/70 text-indigo-900',
    emerald: 'border-emerald-100 bg-emerald-50/70 text-emerald-900',
    amber: 'border-amber-100 bg-amber-50/70 text-amber-900',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone] || 'border-indigo-100 bg-indigo-50/70 text-indigo-900';

  return (
    <div className={`rounded-md border px-3 py-1.5 text-xs ${toneClass}`}>{trDisplay("Nominale stimato tra ")}{trDisplay(formatYears(futureInfo.yearsFromTodayToPeriod))}:{trDisplay(' ')}
      <strong>{trDisplay(formatInputDisplay(futureInfo.monthlyWithdrawal, { decimals: 0 }))}{trDisplay(" €/mese")}</strong>
      {trDisplay(' ')}({trDisplay(formatInputDisplay(futureInfo.annualWithdrawal, { decimals: 0 }))}{trDisplay(" €/anno)")}</div>
  );
};

const buildVisibleWithdrawalPeriods = (formData) => {
  const totalYears = wholeYears(formData.yearsToSimulate);
  const storedPeriods = Array.isArray(formData.withdrawalPeriods) ? formData.withdrawalPeriods : [];
  const periods = [];
  let coveredYears = 0;

  for (let index = 0; index < MAX_WITHDRAWAL_PERIODS; index++) {
    const remainingBefore = Math.max(totalYears - coveredYears, 0);
    if (remainingBefore <= 0) break;

    const storedPeriod = storedPeriods[index] || {};
    const previousMonthly = periods[index - 1]?.monthlyWithdrawal;
    const monthlyWithdrawal = safeNumber(
      storedPeriod.monthlyWithdrawal,
      index === 0 ? DEFAULT_MONTHLY_WITHDRAWAL : previousMonthly || DEFAULT_MONTHLY_WITHDRAWAL
    );
    const storedYears = wholeYears(storedPeriod.years);
    const isLastAllowedPeriod = index === MAX_WITHDRAWAL_PERIODS - 1;
    const years = isLastAllowedPeriod
      ? remainingBefore
      : (storedYears > 0 ? Math.min(storedYears, remainingBefore) : remainingBefore);
    const startYear = coveredYears + 1;
    const endYear = coveredYears + years;
    const tone = getPeriodTone(index);

    periods.push({
      index,
      label: `Periodo ${index + 1}`,
      range: `Anni ${startYear}-${endYear}`,
      startYear,
      endYear,
      remainingBefore,
      years,
      monthlyWithdrawal,
      annualWithdrawal: annualFromMonthly(monthlyWithdrawal),
      ...tone,
    });

    coveredYears += years;
    if (years >= remainingBefore) break;
  }

  return {
    totalYears,
    coveredYears,
    remainingYears: Math.max(totalYears - coveredYears, 0),
    periods,
  };
};

const resizeWithdrawalPeriodsToTotalYears = (formData, nextYears) => {
  if (nextYears === '') {
    return Array.isArray(formData.withdrawalPeriods)
      ? formData.withdrawalPeriods.map(period => ({ ...period }))
      : [];
  }

  const targetYears = wholeYears(nextYears);
  if (targetYears <= 0) {
    return [{
      monthlyWithdrawal: safeNumber(formData.withdrawalPeriods?.[0]?.monthlyWithdrawal, DEFAULT_MONTHLY_WITHDRAWAL),
      years: nextYears,
    }];
  }

  const sourcePeriods = Array.isArray(formData.withdrawalPeriods) ? formData.withdrawalPeriods : [];
  const periods = [];
  sourcePeriods.slice(0, MAX_WITHDRAWAL_PERIODS).forEach((period) => {
    const normalizedPeriod = {
      monthlyWithdrawal: safeNumber(
        period.monthlyWithdrawal,
        periods[periods.length - 1]?.monthlyWithdrawal || DEFAULT_MONTHLY_WITHDRAWAL
      ),
      years: wholeYears(period.years),
    };

    if (normalizedPeriod.years > 0) {
      periods.push(normalizedPeriod);
    }
  });

  if (periods.length === 0) {
    return [{ monthlyWithdrawal: DEFAULT_MONTHLY_WITHDRAWAL, years: targetYears }];
  }

  const coveredYears = periods.reduce((sum, period) => sum + wholeYears(period.years), 0);
  if (coveredYears < targetYears) {
    periods[periods.length - 1].years += targetYears - coveredYears;
  }

  return periods;
};

const extendLastPeriodWhenAtLimit = (withdrawalPeriods, totalYears) => {
  const targetYears = wholeYears(totalYears);
  if (!Array.isArray(withdrawalPeriods) || withdrawalPeriods.length < MAX_WITHDRAWAL_PERIODS || targetYears <= 0) {
    return withdrawalPeriods;
  }

  const normalizedPeriods = [];
  withdrawalPeriods.slice(0, MAX_WITHDRAWAL_PERIODS).forEach((period) => {
    const normalizedPeriod = {
      monthlyWithdrawal: safeNumber(
        period?.monthlyWithdrawal,
        normalizedPeriods[normalizedPeriods.length - 1]?.monthlyWithdrawal || DEFAULT_MONTHLY_WITHDRAWAL
      ),
      years: wholeYears(period?.years),
    };

    if (normalizedPeriod.years > 0) {
      normalizedPeriods.push(normalizedPeriod);
    }
  });

  if (normalizedPeriods.length < MAX_WITHDRAWAL_PERIODS) {
    return withdrawalPeriods;
  }

  const coveredYears = normalizedPeriods.reduce((sum, period) => sum + period.years, 0);
  if (coveredYears >= targetYears) {
    return withdrawalPeriods;
  }

  const nextPeriods = withdrawalPeriods.slice(0, MAX_WITHDRAWAL_PERIODS).map(period => ({ ...period }));
  nextPeriods[MAX_WITHDRAWAL_PERIODS - 1].years = wholeYears(nextPeriods[MAX_WITHDRAWAL_PERIODS - 1].years) + (targetYears - coveredYears);
  return nextPeriods;
};

const getGuardrailBoundsForMonthly = (monthlyWithdrawal, formData) => {
  const minPct = safeNumber(formData.guardrailMinPct) / 100;
  const maxPct = safeNumber(formData.guardrailMaxPct) / 100;

  return {
    minMonthly: formData.guardrailMinEnabled ? Math.max(0, monthlyWithdrawal * (1 - minPct)) : null,
    maxMonthly: formData.guardrailMaxEnabled ? Math.max(0, monthlyWithdrawal * (1 + maxPct)) : null,
  };
};

const WithdrawalScheduleTimeline = ({ periods, totalYears, remainingYears, formData }) => {
  useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3 shadow-sm" data-testid="swr-schedule-timeline">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold text-slate-700">{trDisplay("Timeline")}</p>
        <p className="text-xs text-slate-500">{trDisplay(totalYears > 0 ? `${totalYears} anni totali` : 'Imposta gli anni totali')}</p>
      </div>
      <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
        {trDisplay(periods.map((segment) => (
          <div
            key={`bar-${segment.label}-${segment.range}`}
            className={`${segment.barClass} min-w-[10px]`}
            style={{ flexGrow: Math.max(segment.years, 1), flexBasis: 0 }}
            title={trDisplay(`${segment.label}: ${segment.range}`)}
          />
        )))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {trDisplay(periods.map((segment) => {
          const futureInfo = getFutureNominalInfo(segment, formData);
          return (
            <div key={`${segment.label}-${segment.range}`} className="flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2">
              <span className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${segment.barClass}`} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <p className="text-xs font-semibold text-slate-700">{trDisplay(segment.label)}</p>
                  <p className="text-[11px] text-slate-500">{trDisplay(segment.range)}</p>
                </div>
                <p className="text-xs text-slate-600">
                  <strong className="text-slate-800">{trDisplay(formatInputDisplay(segment.monthlyWithdrawal, { decimals: 0 }))}{trDisplay(" €/mese")}</strong>
                  {trDisplay(' ')}({trDisplay(formatInputDisplay(segment.annualWithdrawal, { decimals: 0 }))}{trDisplay(" €/anno)")}</p>
                {trDisplay(futureInfo && (
                  <p className="text-[11px] text-indigo-700 mt-0.5">{trDisplay("Nominale indicativo con l’inflazione media tra ")}{trDisplay(formatYears(futureInfo.yearsFromTodayToPeriod))}: {trDisplay(formatInputDisplay(futureInfo.monthlyWithdrawal, { decimals: 0 }))}{trDisplay(" €/mese")}</p>
                ))}
              </div>
            </div>
          );
        }))}
      </div>
      {trDisplay(remainingYears > 0 && (
        <p className="text-xs text-red-600">{trDisplay("Restano ")}{trDisplay(formatYears(remainingYears))}{trDisplay(" da coprire, ma il massimo e' di ")}{trDisplay(MAX_WITHDRAWAL_PERIODS)}{trDisplay(" periodi.")}</p>
      ))}
      {trDisplay(remainingYears === 0 && totalYears > 0 && periods.length === 1 && (
        <p className="text-xs text-slate-500">{trDisplay("Il primo periodo copre tutta la simulazione. Se riduci la durata del periodo, compare automaticamente il periodo successivo.")}</p>
      ))}
    </div>
  );
};

const SWRScenarioForm = ({ onSubmit, onParamsChange, isLoading, progress, restoredScenarioParams, restoreKey, onCancel, hasBackgroundWork = false, configurationLevel, onConfigurationLevelChange, embedded = false }) => {
  useTranslation();
  const [localConfigurationMode, setLocalConfigurationMode] = useState(SWR_UI_MODE_STANDARD);
  const configurationMode = configurationLevel ?? localConfigurationMode;
  const setConfigurationMode = useCallback(mode => { setLocalConfigurationMode(mode); onConfigurationLevelChange?.(mode); }, [onConfigurationLevelChange]);
  const { supportsIntermediate, supportsTechnical, supportsCapitalContributions } = getSWRConfigurationCapabilities(configurationMode);
  const [errors, setErrors] = useState({});
  const [removedContribution, setRemovedContribution] = useState(null);
  const actionAnchorRef = useRef(null);
  const validationSummaryRef = useRef(null);
  const [actionBounds, setActionBounds] = useState(null);
  useEffect(() => {
    const anchor = actionAnchorRef.current;
    const measure = () => {
      const { left, width } = anchor.getBoundingClientRect();
      if (width > 0) setActionBounds(previous => previous?.left === left && previous?.width === width ? previous : { left, width });
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(anchor);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const [formData, setFormData] = useState(() => createDefaultFormData());
  const [initialTaxMode, setInitialTaxMode] = useState('portfolio');
  const [hasVisitedCommonTax, setHasVisitedCommonTax] = useState(true);
  const isAdvancedMode = supportsTechnical;
  const isPersonalizedStrategy = !supportsIntermediate || formData.withdrawalStrategy.id === 'personalized';
  const strategyCapabilities = getSWRWithdrawalStrategyCapabilities(supportsIntermediate ? formData.withdrawalStrategy : undefined);
  const canEditGuardrails = strategyCapabilities.editableGuardrails;
  const nativeGuardrailDefinition = getSWRWithdrawalStrategyDefinition({ ...formData.withdrawalStrategy, version: SWR_WITHDRAWAL_STRATEGY_VERSION });
  const hasStrategyCustomization = formData.strategyCustomizationEnabled;
  const hasCashBuffer = hasStrategyCustomization && supportsIntermediate && formData.dynamicCashBufferEnabled;
  const hasCapitalContributions = supportsCapitalContributions && formData.capitalContributionsEnabled;
  const isHistoricalPortfolio = supportsIntermediate && formData.historicalPortfolio?.enabled === true;
  const canUseAssetGains = isHistoricalPortfolio && isSWRFlowTaxPortfolio({ historicalPortfolio: formData.historicalPortfolio });
  const useAssetGains = canUseAssetGains && initialTaxMode === 'asset';
  const activeHistoricalConfig = useMemo(() => !useAssetGains && isSWRFlowTaxPortfolio({ historicalPortfolio: formData.historicalPortfolio })
    ? { ...formData.historicalPortfolio, taxation: { ...formData.historicalPortfolio.taxation, initialBasisByAsset: {} } }
    : formData.historicalPortfolio, [formData.historicalPortfolio, useAssetGains]);
  const historicalState = useMemo(() => {
    if (!isHistoricalPortfolio) return { prepared: null, error: null };
    try { return { prepared: prepareSWRHistoricalPortfolio(getSWRCustomDraftConfig(activeHistoricalConfig), { cashReturnMode: isAdvancedMode && hasCashBuffer ? formData.cashReturnMode : 'inflation-net', dynamicCashBufferEnabled: hasCashBuffer }), error: null }; }
    catch (e) { return { prepared: null, error: e.message }; }
  }, [isHistoricalPortfolio, activeHistoricalConfig, formData.cashReturnMode, hasCashBuffer, isAdvancedMode]);
  const effectiveInflationPct = historicalState.prepared ? historicalState.prepared.summary.effectiveInflation * 100 : formData.inflationRatePct;
  const effectiveFormData = { ...formData, planningMode: supportsIntermediate ? formData.planningMode : PLANNING_MODE_NOW, inflationRatePct: effectiveInflationPct };

  useEffect(() => {
    if (!restoredScenarioParams || !restoreKey) return;
    setFormData(buildFormDataFromScenarioParams(restoredScenarioParams));
    const specific = hasAssetInitialBases(restoredScenarioParams.historicalPortfolio);
    setInitialTaxMode(specific ? 'asset' : 'portfolio');
    setHasVisitedCommonTax(!specific);
    setRemovedContribution(null);
    setConfigurationMode(resolveSWRConfigurationLevel(restoredScenarioParams));
    setErrors({});
  }, [restoredScenarioParams, restoreKey, setConfigurationMode]);

  const withdrawalSchedule = buildVisibleWithdrawalPeriods(formData);
  const visibleWithdrawalPeriods = withdrawalSchedule.periods;
  const firstPeriodAnnualWithdrawal = visibleWithdrawalPeriods[0]?.annualWithdrawal || 0;
  const isFuturePlanning = supportsIntermediate && formData.planningMode === PLANNING_MODE_FUTURE;
  const yearsUntilStart = isFuturePlanning ? getFuturePlanningYears(formData) : 0;
  const preSimulationInflationFactor = getInflationFactor(effectiveInflationPct, yearsUntilStart);
  const firstPeriodAnnualWithdrawalAtSimulationStart = firstPeriodAnnualWithdrawal * preSimulationInflationFactor;
  const impliedTargetWithdrawalRate = formData.initialCapital > 0
    ? firstPeriodAnnualWithdrawalAtSimulationStart / formData.initialCapital
    : 0;
  const cashBufferTarget = firstPeriodAnnualWithdrawalAtSimulationStart > 0
    ? (firstPeriodAnnualWithdrawalAtSimulationStart / 12) * safeNumber(formData.cashBufferMonths, 12)
    : 0;
  const cashBufferMinimum = firstPeriodAnnualWithdrawalAtSimulationStart > 0
    ? (firstPeriodAnnualWithdrawalAtSimulationStart / 12) * CASH_BUFFER_MINIMUM_MONTHS
    : 0;
  const selectedModelPortfolio = getPortfolioPresetById(formData.portfolioPreset);
  const isCustomPortfolio = formData.portfolioPreset === PORTFOLIO_PRESET_CUSTOM || !selectedModelPortfolio;
  const isStochasticMacro = isHistoricalPortfolio || (isAdvancedMode
    && formData.macroMode === SWR_MACRO_MODE_STOCHASTIC_EURO_V1);

  const buildParams = useCallback((includeDrawdown = false) => {
    const schedule = buildVisibleWithdrawalPeriods(formData);
    const planningMode = supportsIntermediate && formData.planningMode === PLANNING_MODE_FUTURE
      ? PLANNING_MODE_FUTURE
      : PLANNING_MODE_NOW;
    const yearsUntilStart = planningMode === PLANNING_MODE_FUTURE
      ? wholeYears(formData.yearsUntilStart)
      : 0;
    const preSimulationInflationFactor = getInflationFactor(effectiveInflationPct, yearsUntilStart);
    const withdrawalPeriodsToday = schedule.periods.map(period => ({
      years: period.years,
      annualWithdrawal: period.annualWithdrawal,
      monthlyWithdrawal: period.monthlyWithdrawal,
    }));
    const withdrawalPeriods = withdrawalPeriodsToday.map(period => ({
      ...period,
      annualWithdrawal: period.annualWithdrawal * preSimulationInflationFactor,
      monthlyWithdrawal: period.monthlyWithdrawal * preSimulationInflationFactor,
      todayAnnualWithdrawal: period.annualWithdrawal,
      todayMonthlyWithdrawal: period.monthlyWithdrawal,
    }));
    const firstAnnualWithdrawalToday = withdrawalPeriodsToday[0]?.annualWithdrawal || 0;
    const firstAnnualWithdrawal = withdrawalPeriods[0]?.annualWithdrawal || 0;
    const dynamicCashBufferEnabled = hasCashBuffer;
    const useAdvancedCashModel = isAdvancedMode && hasCashBuffer;
    const customGuardrailsEnabled = hasStrategyCustomization && canEditGuardrails && formData.guardrailsEnabled;
    const minPct = customGuardrailsEnabled ? safeNumber(formData.guardrailMinPct) / 100 : 0;
    const maxPct = customGuardrailsEnabled ? safeNumber(formData.guardrailMaxPct) / 100 : 0;
    const customBayesianEnabled = isAdvancedMode
      && !isHistoricalPortfolio
      && isCustomPortfolio
      && formData.customReturnParameterUncertaintyEnabled;
    const effectiveTaxRatePct = isAdvancedMode
      ? formData.taxRatePct
      : (formData.legacySavedPresetTaxRatePct ?? selectedModelPortfolio?.taxRatePct ?? SWR_STANDARD_CUSTOM_TAX_RATE_PCT);
    const effectiveInitialGainFractionPct = formData.initialGainFractionPct;

    return {
      planningMode,
      valuationDate: formData.valuationDate,
      futurePlanningEnabled: planningMode === PLANNING_MODE_FUTURE,
      yearsUntilStart,
      preSimulationInflationFactor,
      initialCapitalTiming: planningMode === PLANNING_MODE_FUTURE ? 'simulation_start_future_value' : 'immediate',
      initialCapital: formData.initialCapital,
      capitalContributions: hasCapitalContributions ? formData.capitalContributions.map(event => ({
        month: (event.year - 1) * 12 + event.month, amount: event.amount, label: event.label,
      })) : [],
      ...(isHistoricalPortfolio ? { historicalPortfolio: getSWRCustomDraftConfig(activeHistoricalConfig) } : {}),
      ...(!isHistoricalPortfolio && selectedModelPortfolio?.taxProfile && formData.legacySavedPresetTaxRatePct == null
        && Math.abs(effectiveTaxRatePct - selectedModelPortfolio.taxRatePct) < 1e-9 ? { simpleTaxProfile: selectedModelPortfolio.taxProfile } : {}),
      portfolioPreset: isHistoricalPortfolio ? 'custom' : formData.portfolioPreset,
      modelPortfolio: !isHistoricalPortfolio && selectedModelPortfolio ? {
        id: selectedModelPortfolio.id,
        equityPct: selectedModelPortfolio.equityPct,
        bondPct: selectedModelPortfolio.bondPct,
        expectedReturnPct: selectedModelPortfolio.expectedReturnPct,
        annualVolatilityPct: selectedModelPortfolio.annualVolatilityPct,
        taxRatePct: selectedModelPortfolio.taxRatePct,
        calibration: selectedModelPortfolio.calibration || null,
      } : null,
      customReturnParameterUncertaintyEnabled: customBayesianEnabled,
      returnParameterUncertaintyEnabled: Boolean(
        !isHistoricalPortfolio && (selectedModelPortfolio || customBayesianEnabled)
      ),
      returnParameterUncertaintyMode: isHistoricalPortfolio ? 'historical-block-bootstrap' : selectedModelPortfolio
        ? 'model-portfolio-required'
        : (customBayesianEnabled ? 'custom-opt-in' : 'custom-fixed'),
      expectedReturn: historicalState.prepared?.summary.appliedStatistics.arithmeticMean ?? formData.expectedReturnPct / 100,
      annualVolatility: historicalState.prepared?.summary.appliedStatistics.annualVolatility ?? formData.annualVolatilityPct / 100,
      maxSingleYearDrawdown: !isAdvancedMode || isHistoricalPortfolio
        || formData.maxSingleYearDrawdownPct === ''
        || formData.maxSingleYearDrawdownPct === null
        ? null
        : formData.maxSingleYearDrawdownPct / 100,
      inflationRate: effectiveInflationPct / 100,
      macroMode: isHistoricalPortfolio ? SWR_MACRO_MODE_HISTORICAL_JOINT : isAdvancedMode && formData.macroMode === SWR_MACRO_MODE_STOCHASTIC_EURO_V1
        ? SWR_MACRO_MODE_STOCHASTIC_EURO_V1
        : SWR_MACRO_MODE_FIXED,
      taxRate: safeNumber(effectiveTaxRatePct, SWR_STANDARD_CUSTOM_TAX_RATE_PCT) / 100,
      annualPortfolioCostRate: isAdvancedMode ? formData.annualPortfolioCostRatePct / 100 : 0,
      annualWealthTaxRate: isAdvancedMode ? formData.annualWealthTaxRatePct / 100 : 0.002,
      minimumIncomeCoverage: isAdvancedMode ? formData.minimumIncomeCoveragePct / 100 : 1,
      safeWithdrawalRiskMetric: SWR_RISK_CAPITAL_DEPLETION,
      swrConfigurationMode: configurationMode,
      withdrawalStrategy: normalizeSWRWithdrawalStrategy(supportsIntermediate ? { ...formData.withdrawalStrategy,
        ...((!isPersonalizedStrategy && (customGuardrailsEnabled || hasCashBuffer)) ? { version: SWR_WITHDRAWAL_STRATEGY_VERSION } : {}) } : undefined),
      withdrawalCustomization: hasStrategyCustomization && supportsIntermediate && formData.withdrawalCustomizationEnabled ? {
        version: 'real-monthly-floor-v1',
        minimumMonthlyRealIncome: formData.minimumMonthlyRealIncome,
      } : null,
      cashReturnMode: useAdvancedCashModel ? formData.cashReturnMode : 'inflation-net',
      cashAnnualReturn: useAdvancedCashModel && formData.cashReturnMode !== 'inflation-net' ? formData.cashAnnualReturnPct / 100 : effectiveInflationPct / 100,
      cashLongRunReturn: useAdvancedCashModel && formData.cashReturnMode === 'money-market-lagged' ? formData.cashLongRunReturnPct / 100 : SWR_CASH_DEFAULTS.cashLongRunReturn,
      cashRateHalfLifeMonths: useAdvancedCashModel && formData.cashReturnMode === 'money-market-lagged' ? formData.cashRateHalfLifeMonths : SWR_CASH_DEFAULTS.cashRateHalfLifeMonths,
      cashInflationPassThrough: useAdvancedCashModel && formData.cashReturnMode === 'money-market-lagged' ? formData.cashInflationPassThroughPct / 100 : SWR_CASH_DEFAULTS.cashInflationPassThrough,
      cashTaxRate: useAdvancedCashModel && formData.cashReturnMode !== 'inflation-net' ? formData.cashTaxRatePct / 100 : 0,
      cashBufferFunding: hasCashBuffer ? formData.cashBufferFunding : 'existing-cash',
      initialGainFraction: safeNumber(
        effectiveInitialGainFractionPct,
        SWR_STANDARD_INITIAL_GAIN_FRACTION_PCT
      ) / 100,
      yearsToSimulate: formData.yearsToSimulate,
      numberOfSimulations: isAdvancedMode
        ? formData.numberOfSimulations
        : SWR_STANDARD_SIMULATIONS,
      realWithdrawalYear0: firstAnnualWithdrawal,
      realWithdrawalYear0Today: firstAnnualWithdrawalToday,
      withdrawalScheduleEnabled: withdrawalPeriods.length > 1,
      withdrawalPeriods,
      withdrawalPeriodsToday,
      withdrawalPolicy: customGuardrailsEnabled ? 'guardrails' : 'fixed',
      guardrailsEnabled: customGuardrailsEnabled,
      guardrailMinEnabled: customGuardrailsEnabled && formData.guardrailMinEnabled,
      guardrailMaxEnabled: customGuardrailsEnabled && formData.guardrailMaxEnabled,
      guardrailMinPct: minPct,
      guardrailMaxPct: maxPct,
      dynamicCashBufferEnabled,
      cashBufferMonths: dynamicCashBufferEnabled ? safeNumber(formData.cashBufferMonths, 12) : 12,
      minRealWithdrawalYear0: customGuardrailsEnabled && formData.guardrailMinEnabled ? Math.max(0, firstAnnualWithdrawal * (1 - minPct)) : null,
      maxRealWithdrawalYear0: customGuardrailsEnabled && formData.guardrailMaxEnabled ? Math.max(0, firstAnnualWithdrawal * (1 + maxPct)) : null,
      randomSeed: isAdvancedMode ? formData.randomSeed : '',
      ...(formData.restoredAutomaticSeed != null ? { effectiveRandomSeed: formData.restoredAutomaticSeed } : {}),
      ...(includeDrawdown ? { enableDrawdownAnalysis: true } : {}),
    };
  }, [formData, configurationMode, supportsIntermediate, hasCapitalContributions, isAdvancedMode, isPersonalizedStrategy, canEditGuardrails, hasStrategyCustomization, hasCashBuffer, isCustomPortfolio, selectedModelPortfolio, isHistoricalPortfolio, activeHistoricalConfig, historicalState.prepared, effectiveInflationPct]);

  useEffect(() => {
    if (onParamsChange) {
      onParamsChange(buildParams(false));
    }
  }, [buildParams, onParamsChange]);

  const handleConfigurationModeChange = useCallback(mode => {
    setConfigurationMode(mode);
  }, [setConfigurationMode]);
  useEffect(() => { setErrors({}); }, [configurationMode]);

  const handleChange = useCallback((field, value) => {
    const nextFormData = (() => {
      if (field === 'macroMode' && value === SWR_MACRO_MODE_FIXED && formData.cashReturnMode === 'macro-proxy') return { ...formData, macroMode: value, cashReturnMode: 'fixed' };
      return { ...formData, [field]: value };
    })();
    const validationFormData = isAdvancedMode
      ? nextFormData
      : { ...nextFormData, macroMode: SWR_MACRO_MODE_FIXED };

    setFormData(prev => {
      if (field === 'macroMode' && value === SWR_MACRO_MODE_FIXED && prev.cashReturnMode === 'macro-proxy') {
        return { ...prev, macroMode: value, cashReturnMode: 'fixed' };
      }
      return { ...prev, [field]: value,
        ...(field === 'minimumMonthlyRealIncome' ? { withdrawalCustomizationEnabled: value !== '' && value != null } : {}),
        ...(field === 'randomSeed' ? { restoredAutomaticSeed: null } : {}) };
    });
    setErrors(prev => ({
      ...prev,
      [field]: getRangeError(field, value, validationFormData),
      ...(field === 'macroMode' ? {
        inflationRatePct: getRangeError('inflationRatePct', validationFormData.inflationRatePct, validationFormData),
      } : {}),
    }));
  }, [formData, isAdvancedMode]);

  const handleHistoricalPortfolioChange = next => {
    if (isSWRFlowTaxPortfolio({ historicalPortfolio: next }) && (initialTaxMode === 'asset' || hasAssetInitialBases(next))) {
      const previous = formData.historicalPortfolio;
      const reference = next.taxation.basisReferenceCapital || formData.initialCapital || 1;
      const bases = {};
      for (const row of next.allocations) {
        if (!row.assetId || !Number.isFinite(row.weight) || row.weight <= 0) continue;
        const oldRow = previous.allocations.find(a => a.assetId === row.assetId);
        const saved = next.taxation.initialBasisByAsset[row.assetId];
        bases[row.assetId] = saved != null
          ? oldRow?.weight > 0 && oldRow.weight !== row.weight ? saved * row.weight / oldRow.weight : saved
          : reference * row.weight * (1 - (Number.isFinite(formData.initialGainFractionPct) ? formData.initialGainFractionPct / 100 : 0));
      }
      next = { ...next, taxation: { ...next.taxation, basisReferenceCapital: reference, initialBasisByAsset: bases } };
    }
    handleChange('historicalPortfolio', next);
  };

  const handleInitialTaxModeChange = mode => {
    if (mode === 'asset' && !hasAssetInitialBases(formData.historicalPortfolio)) {
      const reference = formData.initialCapital || 1;
      handleChange('historicalPortfolio', { ...formData.historicalPortfolio, taxation: {
        ...formData.historicalPortfolio.taxation, basisReferenceCapital: reference,
        initialBasisByAsset: Object.fromEntries(formData.historicalPortfolio.allocations.filter(a => a.assetId && Number.isFinite(a.weight) && a.weight > 0).map(a =>
          [a.assetId, reference * a.weight * (1 - (Number.isFinite(formData.initialGainFractionPct) ? formData.initialGainFractionPct / 100 : 0))])),
      } });
    }
    if (mode === 'portfolio' && !hasVisitedCommonTax) {
      handleChange('initialGainFractionPct', portfolioGainFromBases(formData.historicalPortfolio, formData.initialCapital, formData.initialGainFractionPct / 100));
      setHasVisitedCommonTax(true);
    }
    setInitialTaxMode(mode);
    setErrors(prev => ({ ...prev, initialGainFractionPct: null, historicalPortfolio: null }));
  };

  const debtConfiguration = isHistoricalPortfolio && isSWRDebtPortfolio({ historicalPortfolio: formData.historicalPortfolio });
  const grossFiscalCapital = debtConfiguration ? formData.initialCapital * formData.historicalPortfolio.allocations.reduce((s, a) => s + (a.assetId && Number.isFinite(a.weight) ? a.weight : 0), 0) : formData.initialCapital;
  const initialGainsEditor = <SWRInitialGains capital={grossFiscalCapital} leveraged={debtConfiguration} gainPct={formData.initialGainFractionPct}
    onChange={v => handleChange('initialGainFractionPct', v)} error={errors.initialGainFractionPct}
    allowAssets={canUseAssetGains} mode={initialTaxMode} onModeChange={handleInitialTaxModeChange}
    assetSummaryPct={debtConfiguration || Math.abs(formData.historicalPortfolio.allocations.reduce((s, a) => s + (a.assetId && Number.isFinite(a.weight) ? a.weight : 0), 0) - 1) < 1e-8
      ? portfolioGainFromBases(formData.historicalPortfolio, formData.initialCapital, formData.initialGainFractionPct / 100) : NaN} />;

  const handleDraftRangeChange = useCallback((field, value) => {
    const draftValue = value === null ? '' : value;
    setErrors(prev => ({
      ...prev,
      [field]: getRangeError(field, field === 'yearsToSimulate' && draftValue !== '' ? Math.round(draftValue) : draftValue, {
        ...formData,
        [field]: draftValue,
      }),
    }));
  }, [formData]);

  const handlePlanningModeChange = useCallback((mode) => {
    setFormData(prev => ({
      ...prev,
      planningMode: mode,
      yearsUntilStart: mode === PLANNING_MODE_FUTURE && isEmptyInput(prev.yearsUntilStart)
        ? 10
        : prev.yearsUntilStart,
    }));
    setErrors(prev => ({ ...prev, planningMode: null, yearsUntilStart: null }));
  }, []);

  const handlePortfolioPresetChange = useCallback((presetId) => {
    const modelPortfolio = getPortfolioPresetById(presetId);
    setFormData(prev => {
      if (!modelPortfolio) {
        return { ...prev, portfolioPreset: PORTFOLIO_PRESET_CUSTOM, legacySavedPresetTaxRatePct: null };
      }

      return {
        ...prev,
        portfolioPreset: modelPortfolio.id,
        legacySavedPresetTaxRatePct: null,
        expectedReturnPct: modelPortfolio.expectedReturnPct,
        annualVolatilityPct: modelPortfolio.annualVolatilityPct,
        taxRatePct: modelPortfolio.taxRatePct,
      };
    });
    setErrors(prev => ({
      ...prev,
      portfolioPreset: null,
      expectedReturnPct: null,
      annualVolatilityPct: null,
      taxRatePct: null,
    }));
  }, []);

  const handleYearsToSimulateChange = useCallback((value) => {
    const nextYears = value === '' ? '' : Math.round(value);

    setFormData(prev => {
      const withdrawalPeriods = resizeWithdrawalPeriodsToTotalYears(prev, nextYears);
      return { ...prev, yearsToSimulate: nextYears, withdrawalPeriods };
    });
    setErrors(prev => ({
      ...prev,
      yearsToSimulate: getRangeError('yearsToSimulate', nextYears, { ...formData, yearsToSimulate: nextYears }),
      withdrawalPeriods: null,
    }));
  }, [formData]);

  const handleWithdrawalPeriodChange = useCallback((index, field, value) => {
    const scheduleBeforeChange = buildVisibleWithdrawalPeriods(formData);
    const visiblePeriodBeforeChange = scheduleBeforeChange.periods[index];
    const periodError = (() => {
      if (field === 'monthlyWithdrawal') {
        return value !== '' && Number(value) < 0 ? 'Il prelievo mensile non può essere negativo.' : null;
      }
      if (field === 'years') {
        if (value === '') return null;
        const roundedYears = Math.round(value);
        if (!Number.isInteger(roundedYears) || roundedYears < 1) return 'La durata deve essere almeno 1 anno.';
        if (visiblePeriodBeforeChange?.remainingBefore && roundedYears > visiblePeriodBeforeChange.remainingBefore) {
          return `La durata massima da questo punto è ${visiblePeriodBeforeChange.remainingBefore} anni.`;
        }
      }
      return null;
    })();

    setFormData(prev => {
      const schedule = buildVisibleWithdrawalPeriods(prev);
      const withdrawalPeriods = Array.isArray(prev.withdrawalPeriods)
        ? prev.withdrawalPeriods.map(period => ({ ...period }))
        : [];

      for (let i = 0; i <= index; i++) {
        const visiblePeriod = schedule.periods[i];
        const previousPeriod = withdrawalPeriods[i - 1];
        withdrawalPeriods[i] = {
          monthlyWithdrawal: safeNumber(
            withdrawalPeriods[i]?.monthlyWithdrawal,
            visiblePeriod?.monthlyWithdrawal ?? previousPeriod?.monthlyWithdrawal ?? DEFAULT_MONTHLY_WITHDRAWAL
          ),
          years: withdrawalPeriods[i]?.years ?? visiblePeriod?.years ?? visiblePeriod?.remainingBefore ?? 1,
        };
      }

      const visiblePeriod = schedule.periods[index];
      if (field === 'years') {
        withdrawalPeriods[index].years = value === ''
          ? ''
          : Math.max(1, Math.min(Math.round(value), visiblePeriod?.remainingBefore || Math.round(value)));
      } else {
        withdrawalPeriods[index].monthlyWithdrawal = value || 0;
      }

      return {
        ...prev,
        withdrawalPeriods: extendLastPeriodWhenAtLimit(withdrawalPeriods, prev.yearsToSimulate),
      };
    });
    setErrors(prev => ({
      ...prev,
      [`withdrawalPeriod${index}Monthly`]: field === 'monthlyWithdrawal' ? periodError : null,
      [`withdrawalPeriod${index}Years`]: field === 'years' ? periodError : null,
      withdrawalPeriods: null,
    }));
  }, [formData]);

  const progressValue = typeof progress === 'object' ? progress?.percent || 0 : progress || 0;
  const progressPhase = typeof progress === 'object' ? progress?.phase : null;

  const validate = () => {
    const newErrors = {};
    if (isHistoricalPortfolio && historicalState.error) newErrors.historicalPortfolio = historicalState.error;

    if (!formData.initialCapital || formData.initialCapital <= 0) {
      newErrors.initialCapital = isFuturePlanning
        ? 'Il patrimonio disponibile a inizio simulazione deve essere maggiore di 0.'
        : 'Il capitale iniziale deve essere maggiore di 0.';
    }

    if (supportsIntermediate && ![PLANNING_MODE_NOW, PLANNING_MODE_FUTURE].includes(formData.planningMode)) {
      newErrors.planningMode = 'Seleziona una modalità di pianificazione valida.';
    }

    if (isFuturePlanning) {
      if (formData.yearsUntilStart === null || formData.yearsUntilStart === undefined ||
          !Number.isInteger(formData.yearsUntilStart) ||
          formData.yearsUntilStart < 0 || formData.yearsUntilStart > MAX_FUTURE_START_YEARS) {
        newErrors.yearsUntilStart = `Gli anni fino all'inizio devono essere un numero intero tra 0 e ${MAX_FUTURE_START_YEARS}.`;
      }
    }

    if (!isHistoricalPortfolio && (formData.expectedReturnPct < -50 || formData.expectedReturnPct > 50)) {
      newErrors.expectedReturnPct = 'Il rendimento deve essere tra -50% e +50%.';
    }

    if (!isHistoricalPortfolio && (formData.annualVolatilityPct < 0 || formData.annualVolatilityPct > 100)) {
      newErrors.annualVolatilityPct = 'La volatilità deve essere tra 0% e 100%.';
    }

    if (isAdvancedMode && !isHistoricalPortfolio && formData.maxSingleYearDrawdownPct !== '' && formData.maxSingleYearDrawdownPct !== null) {
      const maxDrawdown = parseFloat(formData.maxSingleYearDrawdownPct);
      if (isNaN(maxDrawdown) || maxDrawdown <= 0 || maxDrawdown > 100) {
        newErrors.maxSingleYearDrawdownPct = 'Se specificato, deve essere tra 1 e 100 (es. 30 = max -30%).';
      }
    }

    if (effectiveInflationPct <= -100 || effectiveInflationPct > 100) {
      newErrors.inflationRatePct = 'L’inflazione deve essere maggiore di -100% e non superiore a +100%.';
    }
    if (isStochasticMacro && effectiveInflationPct <= -100) {
      newErrors.inflationRatePct = 'Con inflazione stocastica il valore deve essere strettamente maggiore di -100%.';
    }
    if (isAdvancedMode && !isHistoricalPortfolio && ![SWR_MACRO_MODE_FIXED, SWR_MACRO_MODE_STOCHASTIC_EURO_V1].includes(formData.macroMode)) {
      newErrors.macroMode = 'Seleziona una modalita macroeconomica valida.';
    }

    if (isAdvancedMode && (formData.taxRatePct < 0 || formData.taxRatePct >= 100)) {
      newErrors.taxRatePct = "L'aliquota fiscale deve essere tra 0% e meno del 100%.";
    }

    if (!useAssetGains && (!Number.isFinite(formData.initialGainFractionPct) ||
        formData.initialGainFractionPct < 0 || formData.initialGainFractionPct > 100)) {
      newErrors.initialGainFractionPct = 'La quota di plusvalenza deve essere tra 0% e 100%.';
    }

    if (formData.yearsToSimulate === null || formData.yearsToSimulate === undefined ||
        !Number.isInteger(formData.yearsToSimulate) ||
        formData.yearsToSimulate < 1 || formData.yearsToSimulate > 100) {
      newErrors.yearsToSimulate = 'Gli anni di simulazione devono essere un numero intero tra 1 e 100.';
    }

    if (isAdvancedMode && (formData.numberOfSimulations === null || formData.numberOfSimulations === undefined ||
        !Number.isInteger(formData.numberOfSimulations) ||
        formData.numberOfSimulations < SWR_MAIN_MIN_SIMULATIONS ||
        formData.numberOfSimulations > SWR_MAIN_MAX_SIMULATIONS)) {
      newErrors.numberOfSimulations = `Il numero di simulazioni deve essere un numero intero tra ${SWR_MAIN_MIN_SIMULATIONS.toLocaleString(getLocale())} e ${SWR_MAIN_MAX_SIMULATIONS.toLocaleString(getLocale())}.`;
    }

    if (hasCapitalContributions) {
      try {
        for (const [index, event] of formData.capitalContributions.entries()) {
          if (!Number.isInteger(event.year) || event.year < 1 || !Number.isInteger(event.month) || event.month < 1 || event.month > 12) {
            throw new Error(`Apporto ${index + 1}: scegli un anno del piano e un mese da 1 a 12.`);
          }
        }
        normalizeSWRContributions(buildParams().capitalContributions, formData.yearsToSimulate);
      } catch (error) { newErrors.capitalContributions = error.message; }
    }
    const schedule = buildVisibleWithdrawalPeriods(formData);

    if (schedule.periods.length === 0) {
      newErrors.withdrawalPeriods = 'Imposta almeno un periodo di prelievo.';
    }

    schedule.periods.forEach((period) => {
      if (period.monthlyWithdrawal === null || period.monthlyWithdrawal === undefined || period.monthlyWithdrawal < 0) {
        newErrors[`withdrawalPeriod${period.index}Monthly`] = 'Il prelievo mensile non può essere negativo.';
      }
      if (!Number.isInteger(period.years) || period.years < 1) {
        newErrors[`withdrawalPeriod${period.index}Years`] = 'La durata deve essere almeno 1 anno.';
      }
    });

    if (schedule.remainingYears > 0) {
      newErrors.withdrawalPeriods = `I periodi coprono ${schedule.coveredYears} anni su ${schedule.totalYears}. Aumenta la durata del quarto periodo o riduci gli anni di simulazione.`;
    }

    if (hasStrategyCustomization && supportsIntermediate && formData.withdrawalCustomizationEnabled &&
        (typeof formData.minimumMonthlyRealIncome !== 'number' ||
          !Number.isFinite(formData.minimumMonthlyRealIncome) || formData.minimumMonthlyRealIncome < 0)) {
      newErrors.minimumMonthlyRealIncome = 'Il reddito minimo mensile deve essere un importo maggiore o uguale a zero.';
    }

    if (hasStrategyCustomization && canEditGuardrails && formData.guardrailsEnabled) {
      if (!formData.guardrailMinEnabled && !formData.guardrailMaxEnabled) {
        newErrors.guardrails = 'Attiva almeno il guardrail minimo o quello massimo.';
      }
      if (formData.guardrailMinEnabled && (formData.guardrailMinPct === null || formData.guardrailMinPct === undefined || formData.guardrailMinPct < 0 || formData.guardrailMinPct > 100)) {
        newErrors.guardrailMinPct = 'Il guardrail minimo deve essere tra 0% e 100%.';
      }
      if (formData.guardrailMaxEnabled && (formData.guardrailMaxPct === null || formData.guardrailMaxPct === undefined || formData.guardrailMaxPct < 0 || formData.guardrailMaxPct > 100)) {
        newErrors.guardrailMaxPct = 'Il guardrail massimo deve essere tra 0% e 100%.';
      }
    }

    if (hasCashBuffer) {
      if (formData.cashBufferMonths === null || formData.cashBufferMonths === undefined ||
          !Number.isInteger(formData.cashBufferMonths) ||
          formData.cashBufferMonths < 6 || formData.cashBufferMonths > 36) {
        newErrors.cashBufferMonths = 'Il buffer deve essere un numero intero tra 6 e 36 mesi.';
      }

      const firstMonthlyWithdrawal = (schedule.periods[0]?.monthlyWithdrawal || 0) * preSimulationInflationFactor;
      const targetBuffer = firstMonthlyWithdrawal * safeNumber(formData.cashBufferMonths, 12);
      if (targetBuffer >= formData.initialCapital) {
        newErrors.cashBufferMonths = isFuturePlanning
          ? 'La riserva iniziale stimata a inizio simulazione deve essere inferiore al patrimonio futuro.'
          : 'La riserva iniziale deve essere inferiore al capitale iniziale.';
      }
    }

    for (const [field, min, max] of [
      ...(isAdvancedMode ? [['annualPortfolioCostRatePct', 0, 50], ['annualWealthTaxRatePct', 0, 50], ['minimumIncomeCoveragePct', 1, 100]] : []),
      ...(isAdvancedMode && hasCashBuffer && formData.cashReturnMode !== 'inflation-net' ? [['cashAnnualReturnPct', -99, 100], ['cashTaxRatePct', 0, 100]] : []),
      ...(isAdvancedMode && hasCashBuffer && formData.cashReturnMode === 'money-market-lagged' ? [['cashLongRunReturnPct', -99, 100], ['cashInflationPassThroughPct', 0, 100]] : []),
    ]) {
      if (typeof formData[field] !== 'number' || !Number.isFinite(formData[field]) || formData[field] < min || formData[field] > max) {
        newErrors[field] = `Inserisci una percentuale tra ${min}% e ${max}%.`;
      }
    }
    if (isAdvancedMode && hasCashBuffer && formData.cashReturnMode === 'money-market-lagged' && (!Number.isInteger(formData.cashRateHalfLifeMonths) || formData.cashRateHalfLifeMonths < 1 || formData.cashRateHalfLifeMonths > 120)) newErrors.cashRateHalfLifeMonths = 'Inserisci un numero intero tra 1 e 120 mesi.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(buildParams(true));
    } else {
      requestAnimationFrame(() => {
        const field = actionAnchorRef.current?.querySelector('[data-swr-field-error]');
        const target = field || validationSummaryRef.current;
        for (let parent = target?.parentElement; parent; parent = parent.parentElement) {
          if (parent.tagName === 'DETAILS') parent.open = true;
        }
        target?.scrollIntoView?.({ block: 'center', behavior: 'instant' });
        (field?.querySelector('input, select, textarea') || validationSummaryRef.current)?.focus({ preventScroll: true });
      });
    }
  };

  return (
    <Card className="bg-white shadow-lg border-0 flex flex-col settings-card">
      <CardHeader className={`${embedded ? 'hidden' : ''} border-b border-slate-100 pb-3 sm:pb-4 flex-shrink-0`}>
        <CardTitle className="text-lg sm:text-xl font-semibold text-slate-800 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>{trDisplay("Parametri Safe Withdrawal")}</CardTitle>
      </CardHeader>
      <div className="settings-scroll-area">
        <CardContent className="pt-4 pb-4 px-3 sm:px-6">
          {configurationLevel == null && <SWRConfigurationLevelSelector level={configurationMode} onLevelChange={handleConfigurationModeChange} />}
          <form ref={actionAnchorRef} onSubmit={handleSubmit} className="space-y-6">
            {restoredScenarioParams?.safeWithdrawalRiskMetric === SWR_RISK_MINIMUM_INCOME && <p role="note" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="swr-legacy-risk-notice">{trDisplay('Questo scenario archiviato usava il reddito insufficiente come criterio delle card. I risultati salvati restano invariati; una nuova simulazione userà l’esaurimento del capitale.')}</p>}
            {Object.values(errors).some(Boolean) && <div ref={validationSummaryRef} tabIndex={-1} role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 focus:outline-none" data-testid="swr-form-validation-summary"><span className="font-medium">{trDisplay('Controlla i campi segnalati prima di eseguire la simulazione.')}</span><ul className="mt-1 list-disc pl-5">{Object.entries(errors).filter(([, value]) => value).map(([field, message]) => <li key={field}>{trDisplay(message)}</li>)}</ul></div>}
            <section aria-labelledby="swr-capital-section-heading" data-testid="swr-capital-section" className="swr-configuration-group space-y-4">
              <h2 id="swr-capital-section-heading" className="border-b border-border pb-3 text-lg font-semibold text-slate-800">{trDisplay('Capitale e prelievi')}</h2>
              <div role="group" aria-label={trDisplay("Capitale iniziale e apporti")} data-testid="swr-capital-funding-group" className="rounded-lg border border-border bg-background p-3 sm:p-4 space-y-3">
              <div data-testid="swr-initial-capital-row" className="grid grid-cols-1 gap-4 sm:grid-cols-2"><FormField help={isFuturePlanning ? 'futureCapital' : 'initialCapital'}
              label={trDisplay(debtConfiguration ? 'Patrimonio netto a inizio simulazione (€)' : isFuturePlanning ? 'Patrimonio a inizio simulazione (€)' : 'Capitale iniziale (€)')}
              error={errors.initialCapital}
            >
              <FormattedInput value={formData.initialCapital} onChange={(v) => handleChange('initialCapital', v || 0)} decimals={0} className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("es. 2.000.000")} data-testid="swr-initial-capital" />
              {trDisplay(isFuturePlanning && (
                <p className="text-xs text-slate-500">{trDisplay("Inserisci qui il capitale stimato tra ")}{trDisplay(formatYears(yearsUntilStart))}{trDisplay(", cioè all'inizio effettivo del piano.")}</p>
              ))}
            </FormField><FormField help="horizon" label={trDisplay("Anni di simulazione")} error={errors.yearsToSimulate}>
              <FormattedInput value={formData.yearsToSimulate} onChange={(v) => handleYearsToSimulateChange(v)} onDraftChange={(v) => handleDraftRangeChange('yearsToSimulate', v)} decimals={0} commitOnBlur className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("es. 40")} data-testid="swr-years" />
            </FormField></div>
              <fieldset hidden={!supportsCapitalContributions} disabled={!supportsCapitalContributions}><div className="border-t border-border pt-2" data-testid="swr-contributions-section">
              <div className="flex min-h-11 items-center justify-between gap-3">
                <div className="min-w-0">
                  <Label htmlFor="swr-contributions-toggle" className="text-sm font-semibold">{trDisplay("Apporti di capitale durante il decumulo (")}{trDisplay(formData.capitalContributions.length)})</Label>
                  {!formData.capitalContributionsEnabled && formData.capitalContributions.length > 0 && <p className="text-xs text-muted-foreground">{trDisplay('Apporti in bozza, esclusi dalla simulazione.')}</p>}
                </div>
                <Switch id="swr-contributions-toggle" checked={formData.capitalContributionsEnabled}
                  onCheckedChange={checked => { handleChange('capitalContributionsEnabled', checked); setErrors(prev => ({ ...prev, capitalContributions: null })); }}
                  aria-controls="swr-contributions-fields" aria-describedby="swr-contributions-description"
                  className="relative shrink-0 after:absolute after:-inset-y-3 after:-inset-x-1 after:content-['']" data-testid="swr-contributions-toggle" />
              </div>
              <p id="swr-contributions-description" className="sr-only">{trDisplay('Attiva per includere gli apporti nel piano. Disattivando, i dati restano in bozza e non si applicano.')}</p>
              <fieldset id="swr-contributions-fields" hidden={!hasCapitalContributions} disabled={!hasCapitalContributions} className="space-y-2">
                <div className="flex items-start gap-2">
                  <p className="text-xs text-muted-foreground">{trDisplay(debtConfiguration ? 'Importi netti nominali: all’inizio del mese saldano prima le passività; l’eccedenza è investita.' : 'Importi netti nominali, investiti all’inizio del mese scelto.')}{' '}{trDisplay('Arrivi ipotizzati certi; i prelievi mancati non si recuperano.')}</p>
                </div>
                {formData.capitalContributions.map((event, index) => <details key={index} open={index === formData.capitalContributions.length - 1} className="rounded-md border border-border p-3">
                  <summary className="cursor-pointer text-sm font-medium">{trDisplay("Apporto ")}{trDisplay(index + 1)}{event.label ? ` · ${event.label}` : ''}{trDisplay(" · anno ")}{trDisplay(event.year)}{trDisplay(", mese ")}{trDisplay(event.month)}</summary>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {trDisplay([['year', 'Anno del decumulo'], ['month', 'Mese nell’anno (1–12)'], ['amount', 'Importo nominale netto (€)']].map(([field, label]) => <div key={field} className={`space-y-1 ${field === 'amount' ? 'col-span-2 sm:col-span-1' : ''}`}>
                      <Label htmlFor={`swr-contribution-${index}-${field}`} className="text-xs">{trDisplay(label)}</Label>
                      <FormattedInput id={`swr-contribution-${index}-${field}`} data-testid={`swr-contribution-${index}-${field}`}
                        className="h-11" decimals={field === 'amount' ? 2 : 0} value={event[field]}
                        onChange={value => handleChange('capitalContributions', formData.capitalContributions.map((item, i) => i === index ? { ...item, [field]: value } : item))} />
                    </div>))}
                    <div className="col-span-2 space-y-1 sm:col-span-1">
                      <Label htmlFor={`swr-contribution-${index}-label`} className="text-xs">{trDisplay("Descrizione (facoltativa)")}</Label>
                      <Input id={`swr-contribution-${index}-label`} className="h-11" maxLength={100} value={event.label}
                        onChange={e => handleChange('capitalContributions', formData.capitalContributions.map((item, i) => i === index ? { ...item, label: e.target.value } : item))} />
                    </div>
                  </div>
                  <Button type="button" variant="ghost" className="h-11 text-destructive mt-2" aria-label={trDisplay(`Rimuovi apporto ${index + 1}`)}
                    onClick={() => { setRemovedContribution({ event, index }); handleChange('capitalContributions', formData.capitalContributions.filter((_, i) => i !== index)); }}>{trDisplay("Rimuovi apporto")}</Button>
                </details>)}
                {trDisplay(errors.capitalContributions && <p role="alert" className="text-sm text-destructive">{trDisplay(errors.capitalContributions)}</p>)}
                <div className="flex items-center gap-2">
                <Button type="button" variant="outline" className="h-11" data-testid="swr-add-contribution" disabled={formData.capitalContributions.length >= SWR_MAX_CONTRIBUTIONS}
                  onClick={() => handleChange('capitalContributions', [...formData.capitalContributions, { year: 1, month: 1, amount: '', label: '' }])}>{trDisplay("Aggiungi apporto")}</Button>
                <SWRHelp topic="capitalContributions" buttonTrigger triggerClassName="h-11 w-11 shrink-0 items-center justify-center" triggerLabel={trDisplay('Informazioni sugli apporti di capitale')} />
                </div>
                {trDisplay(formData.capitalContributions.length >= SWR_MAX_CONTRIBUTIONS && <p className="text-xs text-muted-foreground">{trDisplay("Limite di ")}{trDisplay(SWR_MAX_CONTRIBUTIONS)}{trDisplay(" apporti raggiunto. Puoi riunire importi che arrivano nello stesso mese.")}</p>)}
                {trDisplay(removedContribution && <div role="status" className="text-sm">{trDisplay("Apporto rimosso dalla bozza.")}<Button type="button" variant="link" className="h-11" disabled={formData.capitalContributions.length >= SWR_MAX_CONTRIBUTIONS} onClick={() => {
                    const events = [...formData.capitalContributions]; events.splice(removedContribution.index, 0, removedContribution.event);
                    handleChange('capitalContributions', events); setRemovedContribution(null);
                  }}>{trDisplay("Annulla rimozione")}</Button>
                </div>)}
              </fieldset>
            </div></fieldset>
              <fieldset hidden={!isAdvancedMode} disabled={!isAdvancedMode} className="border-t border-border pt-3" data-testid="swr-income-coverage-section">
                <FormField help="incomeCoverage" label={trDisplay("Quota minima del reddito pianificato da coprire (%)")} error={errors.minimumIncomeCoveragePct}>
                  <FormattedInput value={formData.minimumIncomeCoveragePct} onChange={v => handleChange('minimumIncomeCoveragePct', v)} decimals={0} isPercent data-testid="swr-minimum-income" className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                </FormField>
              </fieldset>
              </div>
              <div data-testid="swr-withdrawal-plan" className="rounded-lg border border-border bg-background p-3 sm:p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2"><Label className="text-sm font-semibold text-slate-800">{trDisplay(isPersonalizedStrategy ? 'Piano dei prelievi' : 'Fabbisogni personali e prelievo iniziale')}</Label><SWRHelp topic={isPersonalizedStrategy ? 'schedule' : 'withdrawalStrategy'} /></div>
                    <p className="text-xs text-slate-600 mt-1">{trDisplay(isPersonalizedStrategy ? 'Scegli quanto prelevare e per quanti anni. Se cambi gli anni totali, la timeline mostrata viene allungata o ridotta dalla coda; i periodi temporaneamente fuori orizzonte vengono ripristinati se riallunghi la simulazione.' : 'Il primo importo avvia la strategia. I periodi successivi descrivono il reddito desiderato per misurare la copertura: non reimpostano il prelievo o le regole della strategia.')}</p>
                  </div>
                </div>
                <div className="rounded-md bg-white/80 border border-indigo-100 px-3 py-2 text-xs text-indigo-700 font-semibold">
                  {trDisplay(visibleWithdrawalPeriods.length)}/{trDisplay(MAX_WITHDRAWAL_PERIODS)}{trDisplay(" periodi")}</div>
              </div>

              <div className="space-y-4 pt-2 border-t border-indigo-100">
                <WithdrawalScheduleTimeline
                  periods={visibleWithdrawalPeriods}
                  totalYears={withdrawalSchedule.totalYears}
                  remainingYears={withdrawalSchedule.remainingYears}
                  formData={effectiveFormData}
                />

                {trDisplay(errors.withdrawalPeriods && (
                  <div className="rounded-lg bg-red-50 text-red-600 border border-red-100 p-3 text-xs">
                    {trDisplay(errors.withdrawalPeriods)}
                  </div>
                ))}

                <div className="space-y-2">
                  {trDisplay(visibleWithdrawalPeriods.map((period) => (
                    <div key={period.label} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                      <div className={`h-1 ${period.barClass}`} />
                      <div className="p-3 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                              {trDisplay(period.label)}
                            </span>
                            <span className="text-xs text-slate-500">{trDisplay(period.range)}</span>
                          </div>
                          <span className="text-xs text-slate-500">{trDisplay(formatYears(period.years))}</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="space-y-1 sm:col-span-2">
                            <div className="flex items-center gap-2"><Label htmlFor={`swr-period-${period.index + 1}-monthly`} className="text-xs font-medium text-slate-600">{trDisplay(!isPersonalizedStrategy ? (period.index > 0 ? 'Fabbisogno mensile in euro di oggi' : 'Prelievo mensile iniziale in euro di oggi') : isFuturePlanning ? 'Prelievo mensile in euro di oggi' : 'Prelievo mensile')}</Label><SWRHelp topic={isPersonalizedStrategy ? 'monthlyWithdrawal' : 'withdrawalStrategy'} /></div>
                            <FormattedInput
                              id={`swr-period-${period.index + 1}-monthly`}
                              value={period.monthlyWithdrawal}
                              onChange={(v) => handleWithdrawalPeriodChange(period.index, 'monthlyWithdrawal', v || 0)}
                              decimals={0}
                              className="border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder={trDisplay("es. 4.200")}
                              data-testid={`swr-period-${period.index + 1}-monthly`}
                            />
                            <AnnualAmountHint monthlyValue={period.monthlyWithdrawal} tone={period.hintTone} />
                            <FutureNominalHint period={period} formData={effectiveFormData} tone={period.hintTone} />
                            {trDisplay(errors[`withdrawalPeriod${period.index}Monthly`] && (
                              <p className="text-sm text-red-500">{trDisplay(errors[`withdrawalPeriod${period.index}Monthly`])}</p>
                            ))}
                          </div>

                          <div className="space-y-1">
                            <div className="flex items-center gap-2"><Label htmlFor={`swr-period-${period.index + 1}-years`} className="text-xs font-medium text-slate-600">{trDisplay("Anni")}</Label><SWRHelp topic={isPersonalizedStrategy ? 'periodYears' : 'withdrawalStrategy'} /></div>
                            <FormattedInput
                              id={`swr-period-${period.index + 1}-years`}
                              value={period.years}
                              onChange={(v) => handleWithdrawalPeriodChange(period.index, 'years', v)}
                              decimals={0}
                              className="border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder={trDisplay(`max ${period.remainingBefore}`)}
                              data-testid={`swr-period-${period.index + 1}-years`}
                            />
                            <p className="text-xs text-slate-500">{trDisplay("Max da qui: ")}{trDisplay(period.remainingBefore)}</p>
                            {trDisplay(errors[`withdrawalPeriod${period.index}Years`] && (
                              <p className="text-sm text-red-500">{trDisplay(errors[`withdrawalPeriod${period.index}Years`])}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )))}
                </div>

                <div className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs text-slate-600">
                  <Wallet className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                  <span>
                    {trDisplay(isFuturePlanning ? (
                      <>{trDisplay("Tasso implicito del primo periodo al via: ")}<strong className="text-slate-800">{trDisplay(formatPercentage(impliedTargetWithdrawalRate, { decimals: 2 }))}</strong>{trDisplay(" del patrimonio futuro.")}{trDisplay(' ')}{trDisplay("Input oggi: ")}{trDisplay(formatInputDisplay(firstPeriodAnnualWithdrawal / 12, { decimals: 0 }))}{trDisplay(" €/mese, equivalente stimato al via: ")}{trDisplay(formatInputDisplay(firstPeriodAnnualWithdrawalAtSimulationStart / 12, { decimals: 0 }))}{trDisplay(" €/mese.")}</>
                    ) : (
                      <>{trDisplay("Tasso implicito del primo periodo: ")}<strong className="text-slate-800">{trDisplay(formatPercentage(impliedTargetWithdrawalRate, { decimals: 2 }))}</strong>{trDisplay(" del capitale iniziale.")}</>
                    ))}
                  </span>
                </div>
              </div>
            </div>
              <fieldset hidden={!supportsIntermediate} disabled={!supportsIntermediate}><div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-1 space-y-2" data-testid="swr-planning-mode">
              <div className="swr-planning-mode__row">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-1"><Label className="text-sm font-semibold text-slate-800">{trDisplay("Avvio del piano")}</Label><SWRHelp topic="planning" /></div>
                  <p id="swr-planning-mode-description" className="sr-only">{trDisplay("Scegli se la simulazione parte oggi o se stai stimando un piano che inizierà in futuro.")}</p>
                </div>
              </div>

              <div
                className="swr-compact-segmented grid grid-cols-2"
                role="group"
                aria-label={trDisplay("Modalità pianificazione")}
                aria-describedby="swr-planning-mode-description"
              >
                <button
                  type="button"
                  onClick={() => handlePlanningModeChange(PLANNING_MODE_NOW)}
                  className="swr-compact-segmented__item px-3 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-pressed={formData.planningMode === PLANNING_MODE_NOW}
                  data-testid="swr-planning-now"
                >
                  <span className="block text-sm font-semibold">{trDisplay("Ora")}</span>
                  <span className="sr-only">{trDisplay("Dal primo anno")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handlePlanningModeChange(PLANNING_MODE_FUTURE)}
                  className="swr-compact-segmented__item px-3 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-pressed={formData.planningMode === PLANNING_MODE_FUTURE}
                  data-testid="swr-planning-future"
                >
                  <span className="block text-sm font-semibold">{trDisplay("Futuro")}</span>
                  <span className="sr-only">{trDisplay("Euro di oggi")}</span>
                </button>
              </div>
              </div>

              {trDisplay(errors.planningMode && <p className="text-sm text-red-500">{trDisplay(errors.planningMode)}</p>)}

              {trDisplay(isFuturePlanning && (
                <div className="space-y-3 rounded-lg border border-indigo-100 bg-white p-3">
                  <FormField help="yearsUntilStart" label={trDisplay("Tra quanti anni inizia")} error={errors.yearsUntilStart}>
                    <FormattedInput
                      value={formData.yearsUntilStart}
                      onChange={(v) => handleChange('yearsUntilStart', v === '' ? '' : Math.round(v))}
                      decimals={0}
                      className="border-indigo-100 focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder={trDisplay("es. 10")}
                      data-testid="swr-years-until-start"
                    />
                  </FormField>
                  <div className="rounded-md border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-900">{trDisplay("Gli importi che inserisci nei prelievi restano in euro di oggi. I nominali indicativi al via usano l’inflazione media del ")}{trDisplay(formatPercentage(effectiveInflationPct / 100, { decimals: 1 }))}{trDisplay(" per ")}{trDisplay(formatYears(yearsUntilStart))}{trDisplay(": fattore ")}{trDisplay(formatInputDisplay(preSimulationInflationFactor, { decimals: 2 }))}{trDisplay("x. ")}{trDisplay(isStochasticMacro ? 'Non sono mediane Monte Carlo: nei risultati il pannello macro mostrerà i percentili degli importi al via, generati dai percorsi alternativi di inflazione.' : 'La simulazione userà questo stesso fattore costante.')}{trDisplay(" I valori reali resteranno in euro di oggi.")}</div>
                </div>
              ))}
            </div></fieldset>
              <fieldset hidden={!supportsIntermediate} disabled={!supportsIntermediate} className="space-y-3"><div className="flex items-center gap-2"><h3 className="text-base font-semibold">{trDisplay('Strategia di decumulo')}</h3><SWRHelp topic="withdrawalStrategy" /></div><SWRWithdrawalStrategySelector value={formData.withdrawalStrategy} onChange={value => handleChange('withdrawalStrategy', value)} /></fieldset>
              <div className="rounded-lg border border-border p-3 sm:p-4 space-y-3" data-testid="swr-strategy-customization">
              <div className="flex min-h-11 items-center justify-between gap-3">
                <div>
                  <Label htmlFor="swr-strategy-customization-toggle" className="text-sm font-semibold text-foreground">{trDisplay('Personalizza la strategia')}</Label>
                  <p id="swr-strategy-customization-description" className="mt-1 text-xs text-muted-foreground">{trDisplay(!supportsIntermediate ? 'Scegli se limitare le variazioni dei prelievi con guardrail percentuali.' : isPersonalizedStrategy ? 'Aggiungi un minimo mensile, guardrail percentuali o una riserva di liquidità.' : strategyCapabilities.builtInGuardrails ? 'Aggiungi un minimo mensile o una riserva di liquidità. I guardrail della strategia restano fissi.' : 'Aggiungi un minimo mensile, guardrail percentuali o una riserva di liquidità.')}</p>
                </div>
                <Switch id="swr-strategy-customization-toggle" checked={hasStrategyCustomization}
                  onCheckedChange={checked => { handleChange('strategyCustomizationEnabled', checked); setErrors({}); }}
                  aria-describedby="swr-strategy-customization-description" aria-controls="swr-strategy-customization-fields swr-personalized-rules"
                  className="relative shrink-0 after:absolute after:-inset-y-3 after:-inset-x-1 after:content-['']" data-testid="swr-strategy-customization-toggle" />
              </div>
              <fieldset hidden={!hasStrategyCustomization || !supportsIntermediate} disabled={!hasStrategyCustomization || !supportsIntermediate} id="swr-strategy-customization-fields" className={STRATEGY_OPTION_CARD_CLASS} data-swr-strategy-option="minimum">
                <div className="swr-minimum-income__row">
                  <div className="flex min-w-0 items-center gap-2">
                    <Wallet className="h-5 w-5 shrink-0 text-blue-600" />
                    <Label htmlFor="swr-minimum-monthly-real-income" className="text-sm font-semibold text-slate-800">{trDisplay('Reddito minimo mensile (€)')}</Label>
                    <SWRHelp topic="minimumMonthlyIncome" buttonTrigger triggerClassName="h-11 w-11 shrink-0 items-center justify-center" triggerLabel={trDisplay('Informazioni sul reddito minimo mensile')} />
                  </div>
                <FormattedInput id="swr-minimum-monthly-real-income" value={formData.minimumMonthlyRealIncome}
                  onChange={value => handleChange('minimumMonthlyRealIncome', value)} decimals={2}
                  aria-describedby="swr-minimum-monthly-real-income-description swr-minimum-monthly-real-income-attention"
                  aria-invalid={Boolean(errors.minimumMonthlyRealIncome)}
                  placeholder={trDisplay('Nessun minimo')}
                  className="h-11 border-blue-100 focus:border-blue-500 focus:ring-blue-500" data-testid="swr-minimum-monthly-real-income" />
                </div>
                <p className="text-xs text-slate-600">{trDisplay('Facoltativo · netto in euro di oggi, indicizzato all’inflazione. Può aumentare il rischio di esaurimento.')}</p>
                <p id="swr-minimum-monthly-real-income-description" className="sr-only">{trDisplay('Importo netto in euro di oggi, rivalutato con l’inflazione anche nell’attesa di un piano futuro. Ogni mese si richiede il maggiore tra questo minimo rivalutato e il prelievo previsto dalla strategia.')}</p>
                <p id="swr-minimum-monthly-real-income-attention" className="sr-only">{trDisplay('Il minimo limita i tagli della strategia e può aumentare il rischio di esaurimento. Il pagamento dipende dal capitale disponibile: non è un reddito garantito.')}</p>
                {errors.minimumMonthlyRealIncome && <p className="text-sm text-destructive" role="alert">{trDisplay(errors.minimumMonthlyRealIncome)}</p>}
              </fieldset>

              <fieldset hidden={!hasStrategyCustomization} disabled={!hasStrategyCustomization}><div id="swr-personalized-rules" className="space-y-3" data-testid="swr-personalized-rules">
              {supportsIntermediate && strategyCapabilities.builtInGuardrails && <div className={STRATEGY_OPTION_CARD_CLASS} data-testid="swr-native-guardrails" id="swr-native-guardrails">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="min-w-0 space-y-1">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">{trDisplay('Guardrail della strategia · fissi')}<SWRHelp topic={formData.withdrawalStrategy.id === 'vanguard' ? 'vanguardGuardrails' : 'guytonKlinger'} /></p>
                    <p className="text-xs text-slate-600">{trDisplay(nativeGuardrailDefinition.fixedRules)}</p>
                  </div>
                </div>
              </div>}

            <fieldset hidden={!canEditGuardrails} disabled={!canEditGuardrails}><div className={STRATEGY_OPTION_CARD_CLASS} data-swr-strategy-option="guardrails">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2"><Label className="text-sm font-semibold text-slate-800">{trDisplay("Guardrail percentuali")}</Label><SWRHelp topic={isPersonalizedStrategy ? "customGuardrails" : "strategySpendingBounds"} /></div>
                    <p className="text-xs text-slate-600 mt-1">
                      {trDisplay(isPersonalizedStrategy ? 'Imposta una fascia percentuale una sola volta: verra applicata al prelievo di ogni periodo.' : 'Limita il prelievo della strategia entro una fascia attorno al fabbisogno di ogni periodo, rivalutata mensilmente. Il minimo mensile personale, se maggiore, prevale.')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.guardrailsEnabled}
                  onCheckedChange={(checked) => handleChange('guardrailsEnabled', checked)}
                  data-testid="swr-guardrails-toggle"
                />
              </div>

              {trDisplay(formData.guardrailsEnabled && (
                <div className="space-y-3 pt-2 border-t border-blue-100">
                  {trDisplay(errors.guardrails && <p className="text-sm text-red-500">{trDisplay(errors.guardrails)}</p>)}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-lg bg-white/75 border border-blue-100 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2"><Label className="text-sm font-semibold text-slate-800">{trDisplay("Limite minimo")}</Label><SWRHelp topic={isPersonalizedStrategy ? "minimumGuardrail" : "strategySpendingBounds"} /></div>
                        <Switch checked={formData.guardrailMinEnabled} onCheckedChange={(checked) => handleChange('guardrailMinEnabled', checked)} data-testid="swr-min-guardrail-toggle" />
                      </div>
                      {trDisplay(formData.guardrailMinEnabled && (
                        <FormField help={isPersonalizedStrategy ? "reduction" : "strategySpendingBounds"} label={trDisplay("Riduzione massima (%)")} error={errors.guardrailMinPct}>
                          <FormattedInput value={formData.guardrailMinPct} onChange={(v) => handleChange('guardrailMinPct', v || 0)} decimals={0} isPercent className="border-blue-100 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("es. 10%")} data-testid="swr-min-guardrail-pct" />
                        </FormField>
                      ))}
                    </div>

                    <div className="rounded-lg bg-white/75 border border-blue-100 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2"><Label className="text-sm font-semibold text-slate-800">{trDisplay("Limite massimo")}</Label><SWRHelp topic={isPersonalizedStrategy ? "maximumGuardrail" : "strategySpendingBounds"} /></div>
                        <Switch checked={formData.guardrailMaxEnabled} onCheckedChange={(checked) => handleChange('guardrailMaxEnabled', checked)} data-testid="swr-max-guardrail-toggle" />
                      </div>
                      {trDisplay(formData.guardrailMaxEnabled && (
                        <FormField help={isPersonalizedStrategy ? "increase" : "strategySpendingBounds"} label={trDisplay("Aumento massimo (%)")} error={errors.guardrailMaxPct}>
                          <FormattedInput value={formData.guardrailMaxPct} onChange={(v) => handleChange('guardrailMaxPct', v || 0)} decimals={0} isPercent className="border-blue-100 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("es. 10%")} data-testid="swr-max-guardrail-pct" />
                        </FormField>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/75 border border-blue-100 p-3 space-y-3">
                    <p className="text-xs font-semibold text-slate-700">{trDisplay("Valori in euro applicati ai periodi")}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {trDisplay(visibleWithdrawalPeriods.map((period) => {
                        const bounds = getGuardrailBoundsForMonthly(period.monthlyWithdrawal, formData);
                        return (
                          <div key={`guardrail-${period.label}`} className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                            <p className="text-xs font-semibold text-slate-700">{trDisplay(period.label)}</p>
                            <p className="text-xs text-slate-500">{trDisplay(period.range)}</p>
                            <p className="text-sm font-semibold text-slate-800 mt-1">{trDisplay("Target ")}{trDisplay(formatInputDisplay(period.monthlyWithdrawal, { decimals: 0 }))}{trDisplay(" €/mese")}</p>
                            <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                              {trDisplay(bounds.minMonthly !== null && (
                                <p>{trDisplay("Min ")}{trDisplay(formatInputDisplay(bounds.minMonthly, { decimals: 0 }))}{trDisplay(" €/mese")}{trDisplay(isFuturePlanning && (() => {
                                    const futureInfo = getFutureNominalInfo(
                                      { ...period, monthlyWithdrawal: bounds.minMonthly, annualWithdrawal: bounds.minMonthly * 12 },
                                      effectiveFormData
                                    );
                                    return futureInfo ? ` (${formatInputDisplay(futureInfo.monthlyWithdrawal, { decimals: 0 })} €/mese nominali indicativi con l’inflazione media)` : '';
                                  })())}
                                </p>
                              ))}
                              {trDisplay(bounds.maxMonthly !== null && (
                                <p>{trDisplay("Max ")}{trDisplay(formatInputDisplay(bounds.maxMonthly, { decimals: 0 }))}{trDisplay(" €/mese")}{trDisplay(isFuturePlanning && (() => {
                                    const futureInfo = getFutureNominalInfo(
                                      { ...period, monthlyWithdrawal: bounds.maxMonthly, annualWithdrawal: bounds.maxMonthly * 12 },
                                      effectiveFormData
                                    );
                                    return futureInfo ? ` (${formatInputDisplay(futureInfo.monthlyWithdrawal, { decimals: 0 })} €/mese nominali indicativi con l’inflazione media)` : '';
                                  })())}
                                </p>
                              ))}
                            </div>
                          </div>
                        );
                      }))}
                    </div>
                  </div>
                </div>
              ))}
            </div></fieldset>

            <fieldset hidden={!supportsIntermediate} disabled={!supportsIntermediate}><div className={STRATEGY_OPTION_CARD_CLASS} data-swr-strategy-option="buffer">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <PiggyBank className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-semibold text-slate-800">{trDisplay("Dynamic Cash Buffer Strategy")}</Label>
                      <SWRHelp topic="buffer" />
                      <SWRHelp topic="bufferResearch"><GraduationCap className="h-4 w-4 text-blue-600 hover:text-blue-800 transition-colors" /></SWRHelp>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{trDisplay("Usa una riserva liquida inclusa nel capitale per evitare vendite automatiche del portafoglio nei mesi di mercato negativo.")}{trDisplay(isFuturePlanning ? ' In questa modalita\' la riserva e\' calcolata sugli importi rivalutati al via.' : '')}
                    </p>
                  </div>
                </div>
                <Switch checked={formData.dynamicCashBufferEnabled} onCheckedChange={(checked) => handleChange('dynamicCashBufferEnabled', checked)} data-testid="swr-cash-buffer-toggle" />
              </div>

              {trDisplay(formData.dynamicCashBufferEnabled && (
                <div className="space-y-3 pt-2 border-t border-blue-100">
                  <FormField help="bufferMonths" label={trDisplay("Mesi di autonomia del buffer")} error={errors.cashBufferMonths}>
                    <FormattedInput
                      value={formData.cashBufferMonths}
                      onChange={(v) => handleChange('cashBufferMonths', v === '' ? '' : Math.round(v))}
                      decimals={0}
                      className="border-blue-100 focus:border-blue-500 focus:ring-blue-500"
                      placeholder={trDisplay("es. 12")}
                      data-testid="swr-cash-buffer-months"
                    />
                  </FormField>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="rounded-md border border-blue-100 bg-white/80 px-3 py-2">
                      <p className="text-xs text-slate-500">{trDisplay(isFuturePlanning ? 'Riserva iniziale stimata al via' : 'Riserva iniziale stimata')}</p>
                      <p className="text-sm font-semibold text-blue-800">{trDisplay(formatInputDisplay(cashBufferTarget, { decimals: 0 }))} €</p>
                    </div>
                    <div className="rounded-md border border-blue-100 bg-white/80 px-3 py-2">
                      <p className="text-xs text-slate-500">{trDisplay(isFuturePlanning ? 'Buffer minimo stimato al via' : 'Buffer minimo')}</p>
                      <p className="text-sm font-semibold text-blue-800">{trDisplay(formatInputDisplay(cashBufferMinimum, { decimals: 0 }))} € ({trDisplay(CASH_BUFFER_MINIMUM_MONTHS)}{trDisplay(" mesi)")}</p>
                    </div>
                  </div>
                </div>
              ))}
              <fieldset hidden={!hasCashBuffer} disabled={!hasCashBuffer}>{trDisplay(formData.dynamicCashBufferEnabled && (
              <details className="border-t border-blue-100 pt-3" open={!isAdvancedMode}>
                <summary className="cursor-pointer text-sm font-semibold text-slate-800">{trDisplay("Liquidità · ")}{trDisplay(isAdvancedMode ? 'modello e ipotesi del fondo monetario' : 'rendimento netto pari all’inflazione')}</summary>
                {trDisplay(!isAdvancedMode && <p className="mt-3 text-sm text-slate-700" data-testid="swr-simple-cash-assumption">{trDisplay("Il rendimento netto segue l’inflazione: il buffer mantiene il potere d’acquisto per ipotesi. Costi del fondo e imposte sul rendimento sono già inclusi; non vengono sottratti una seconda volta. Anche negli stress questa resta una semplificazione.")}</p>)}<fieldset hidden={!isAdvancedMode} disabled={!isAdvancedMode}>
                  <FormField help="cashModel" label={trDisplay("Modello di rendimento del buffer")}>
                    <select value={formData.cashReturnMode} onChange={event => handleChange('cashReturnMode', event.target.value)} aria-label={trDisplay("Modello di rendimento del buffer")} data-testid="swr-cash-model" className="mt-3 w-full rounded-md border border-slate-200 bg-white p-2 text-sm">
                      <option value="money-market-lagged">{trDisplay("Fondo monetario · risposta graduale")}</option>
                      <option value="fixed">{trDisplay("Tasso nominale costante")}</option>
                      <option value="inflation-net">{trDisplay("Inflazione netta · ipotesi semplice")}</option>
                      <option value="macro-proxy" disabled={!isStochasticMacro}>{trDisplay(isHistoricalPortfolio ? 'Proxy depositi · stessi mesi dello storico' : 'Proxy depositi · richiede inflazione stocastica')}</option>
                    </select>
                  </FormField>
                  <p className="mt-2 text-xs text-slate-600">{trDisplay(describeSWRCashModel({ cashReturnMode: formData.cashReturnMode }))}</p>
                  {trDisplay(formData.cashReturnMode !== 'inflation-net' && <>
                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {trDisplay(['fixed', 'money-market-lagged'].includes(formData.cashReturnMode) && <FormField help="cashRate" label={trDisplay(formData.cashReturnMode === 'fixed' ? 'Rendimento nominale annuo (%)' : 'Tasso annuo iniziale della proiezione (%)')} error={errors.cashAnnualReturnPct}>
                        <FormattedInput value={formData.cashAnnualReturnPct} onChange={v => handleChange('cashAnnualReturnPct', v)} decimals={2} isPercent data-testid="swr-cash-return" className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                      </FormField>)}
                      <FormField help="cashTax" label={trDisplay("Aliquota sui rendimenti del cash (%)")} error={errors.cashTaxRatePct}>
                        <FormattedInput value={formData.cashTaxRatePct} onChange={v => handleChange('cashTaxRatePct', v)} decimals={2} isPercent data-testid="swr-cash-tax" className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                      </FormField>
                    </div>
                    {trDisplay(formData.cashReturnMode === 'money-market-lagged' && <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField help="cashLongRun" label={trDisplay("Tasso annuo di lungo periodo (%)")} error={errors.cashLongRunReturnPct}>
                        <FormattedInput value={formData.cashLongRunReturnPct} onChange={v => handleChange('cashLongRunReturnPct', v)} decimals={2} isPercent data-testid="swr-cash-long-run" className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                      </FormField>
                      <FormField help="cashHalfLife" label={trDisplay("Tempo per dimezzare lo scarto dal tasso obiettivo (mesi)")} error={errors.cashRateHalfLifeMonths}>
                        <FormattedInput value={formData.cashRateHalfLifeMonths} onChange={v => handleChange('cashRateHalfLifeMonths', v)} decimals={0} data-testid="swr-cash-half-life" className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                      </FormField>
                      <FormField help="cashPassThrough" label={trDisplay("Trasmissione degli scostamenti d’inflazione (%)")} error={errors.cashInflationPassThroughPct}>
                        <FormattedInput value={formData.cashInflationPassThroughPct} onChange={v => handleChange('cashInflationPassThroughPct', v)} decimals={0} isPercent data-testid="swr-cash-pass-through" className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                      </FormField>
                      <p className="text-xs text-slate-600">{trDisplay("La risposta usa solo l’inflazione già osservata nei 12 mesi precedenti: nessun adeguamento istantaneo durante una fiammata. I default 2%, 12 mesi e 50% sono ipotesi di scenario, non stime statistiche. Nella pianificazione futura la proiezione parte da oggi.")}</p>
                    </div>)}
                  </>)}
                </fieldset>
                <fieldset className="mt-3 space-y-2 text-sm text-slate-700">
                  <legend className="mb-2 font-medium"><span className="inline-flex items-center gap-2">{trDisplay("Origine della riserva iniziale")}<SWRHelp topic="cashFunding" /></span></legend>
                  {trDisplay([['existing-cash', 'Liquidità già disponibile, inclusa nel capitale iniziale'], ['portfolio-sale', 'Vendita di titoli, con imposte sulle plusvalenze']].map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2"><input type="radio" name={trDisplay("cashBufferFunding")} value={value} checked={formData.cashBufferFunding === value} onChange={() => handleChange('cashBufferFunding', value)} />{trDisplay(label)}</label>
                  )))}
                </fieldset>
              </details>
            ))}</fieldset>
            </div>

            </fieldset></div></fieldset>

            </div>
            </section>
            <section aria-labelledby="swr-market-section-heading" data-testid="swr-market-section" className="swr-configuration-group space-y-4"><h2 id="swr-market-section-heading" className="border-b border-border pb-3 text-lg font-semibold text-slate-800">{trDisplay('Portafoglio e inflazione')}</h2><div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 space-y-4">
              <div className="flex items-start gap-2">
                <PieChart className="h-5 w-5 text-slate-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-semibold text-slate-800">{trDisplay("Rendimento e volatilità")}</Label>
                    <SWRHelp topic={isHistoricalPortfolio ? 'historicalPortfolio' : 'portfolio'} />
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    {trDisplay(isHistoricalPortfolio ? 'Crea il tuo portafoglio scegliendo asset class e percentuali.' : 'Scegli un portafoglio modello o inserisci i valori manualmente.')}
                  </p>
                </div>
              </div>

              {trDisplay(supportsIntermediate && <RadioGroup.Root aria-label={trDisplay("Configurazione del portafoglio")} orientation="horizontal"
                value={isHistoricalPortfolio ? 'custom' : 'simple'}
                className="swr-compact-segmented inline-flex max-w-full"
                onValueChange={mode => handleChange('historicalPortfolio', { ...formData.historicalPortfolio, enabled: mode === 'custom',
                  allocations: mode === 'custom' && !formData.historicalPortfolio.allocations.length ? [{ assetId: '', weight: '' }] : formData.historicalPortfolio.allocations })}>
                {trDisplay([{ id: 'simple', label: 'Semplice' }, { id: 'custom', label: 'Asset class custom' }].map(mode => <RadioGroup.Item
                  key={mode.id} value={mode.id} data-testid={`swr-return-mode-${mode.id}`}
                  className="swr-compact-segmented__item px-3 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:text-sm">
                  {trDisplay(mode.label)}
                </RadioGroup.Item>))}
              </RadioGroup.Root>)}
              <fieldset hidden={isHistoricalPortfolio} disabled={isHistoricalPortfolio}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handlePortfolioPresetChange(PORTFOLIO_PRESET_CUSTOM)}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    isCustomPortfolio
                      ? 'border-blue-300 bg-blue-50 text-blue-900 shadow-sm'
                      : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-white'
                  }`}
                  aria-pressed={isCustomPortfolio}
                  data-testid="swr-portfolio-custom"
                >
                  <span className="block text-sm font-semibold">{trDisplay("Valori manuali")}</span>
                  <span className="block text-xs mt-0.5">{trDisplay("Inserisci rendimento e volatilità")}</span>
                </button>

                {trDisplay(MODEL_PORTFOLIOS.map((portfolio) => {
                  const isSelected = formData.portfolioPreset === portfolio.id;
                  return (
                    <button
                      key={portfolio.id}
                      type="button"
                      onClick={() => handlePortfolioPresetChange(portfolio.id)}
                      className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                        isSelected
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-950 shadow-sm'
                          : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-white'
                      }`}
                      aria-pressed={isSelected}
                      data-testid={`swr-portfolio-${portfolio.id}`}
                    >
                      <span className="block text-sm font-semibold">{trDisplay("Equity/Bond ")}{trDisplay(portfolio.label)}</span>
                      <span className="block text-xs mt-0.5">{trDisplay("Rendimento ")}{trDisplay(formatInputDisplay(portfolio.expectedReturnPct, { decimals: 2 }))}{trDisplay("% · Volatilità ")}{trDisplay(formatInputDisplay(portfolio.annualVolatilityPct, { decimals: 2 }))}%
                      </span>
                      <span className="block text-xs mt-0.5">{trDisplay("Aliquota indicativa ")}{trDisplay(formatInputDisplay(portfolio.taxRatePct, { decimals: 2 }))}{trDisplay("% · ETF 2010–2025")}</span>
                    </button>
                  );
                }))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField help="expectedReturn" label={trDisplay("Rendimento medio annuo atteso (%)")} error={errors.expectedReturnPct}>
                  <FormattedInput
                    value={formData.expectedReturnPct}
                    onChange={(v) => handleChange('expectedReturnPct', v || 0)}
                    decimals={2}
                    isPercent
                    disabled={!isCustomPortfolio}
                    className={`border-slate-200 focus:border-blue-500 focus:ring-blue-500 ${!isCustomPortfolio ? 'bg-slate-50 text-slate-500' : ''}`}
                    placeholder={trDisplay("es. 7%")}
                    data-testid="swr-expected-return"
                  />
                </FormField>

                <FormField help="volatility" label={trDisplay("Volatilità annua (%)")} error={errors.annualVolatilityPct}>
                  <FormattedInput
                    value={formData.annualVolatilityPct}
                    onChange={(v) => handleChange('annualVolatilityPct', v || 0)}
                    decimals={2}
                    isPercent
                    disabled={!isCustomPortfolio}
                    className={`border-slate-200 focus:border-blue-500 focus:ring-blue-500 ${!isCustomPortfolio ? 'bg-slate-50 text-slate-500' : ''}`}
                    placeholder={trDisplay("es. 15%")}
                    data-testid="swr-volatility"
                  />
                </FormField>
              </div>

              {trDisplay(!isCustomPortfolio && selectedModelPortfolio && (
                <div className="rounded-md border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900">{trDisplay("Valori applicati dal modello ")}{trDisplay(selectedModelPortfolio.equityPct)}{trDisplay("% equity / ")}{trDisplay(selectedModelPortfolio.bondPct)}{trDisplay("% bond: rendimento atteso ")}{trDisplay(formatInputDisplay(selectedModelPortfolio.expectedReturnPct, { decimals: 2 }))}{trDisplay("% e volatilità attesa ")}{trDisplay(formatInputDisplay(selectedModelPortfolio.annualVolatilityPct, { decimals: 2 }))}%.
                  {trDisplay(selectedModelPortfolio.calibration && <p className="mt-1">{trDisplay("Rendimento composto storico (CAGR): ")}{trDisplay(formatInputDisplay(selectedModelPortfolio.cagrPct, { decimals: 2 }))}{trDisplay("% annuo. È distinto dalla media aritmetica usata come input.")}</p>)}
                  <p className="mt-1" data-testid="swr-preset-source">{trDisplay(selectedModelPortfolio.calibration
                    ? 'NAV ETF 2010–2025 · 16 anni · EUR · ribilanciamento annuale. I costi dei fondi sono già nei rendimenti: inserisci soltanto eventuali costi aggiuntivi.'
                    : 'Preset precedente non verificato: valori originali conservati. Seleziona uno dei nuovi mix ETF per usare la calibrazione riproducibile.')}</p>
                </div>
              ))}

              {trDisplay(isAdvancedMode && (
              <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs text-blue-950">
                <GraduationCap className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="font-semibold">{trDisplay("Incertezza bayesiana sui parametri")}</span>
                      <SWRHelp topic="parameterUncertainty" />
                    </div>
                    <Switch
                      checked={!isCustomPortfolio || formData.customReturnParameterUncertaintyEnabled}
                      disabled={!isCustomPortfolio}
                      onCheckedChange={(checked) => handleChange('customReturnParameterUncertaintyEnabled', checked)}
                      aria-label={trDisplay("Incertezza bayesiana sui parametri")}
                      data-testid="swr-parameter-uncertainty-toggle"
                    />
                  </div>
                  <p className="mt-1 text-blue-800">
                    {trDisplay(!isCustomPortfolio
                      ? (selectedModelPortfolio?.calibration ? 'Sempre attiva · 16 log-rendimenti annui osservati, 2010–2025.' : 'Sempre attiva per il preset precedente · 40 osservazioni equivalenti convenzionali.')
                      : formData.customReturnParameterUncertaintyEnabled
                        ? 'Attiva sui valori manuali · 40 osservazioni equivalenti convenzionali.'
                        : 'Disattivata: rendimento e volatilità manuali restano fissi tra i percorsi.')}
                  </p>
                </div>
              </div>
              ))}

              {trDisplay(!isAdvancedMode && !isCustomPortfolio && selectedModelPortfolio && (
                <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-900">
                  <GraduationCap className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                  <p>
                    {trDisplay(selectedModelPortfolio.calibration
                      ? 'Il mix ETF include l’incertezza dei parametri ricavata dai 16 rendimenti annuali osservati. Le medie storiche sono ipotesi iniziali, non previsioni.'
                      : 'Il preset precedente mantiene l’incertezza convenzionale di 40 osservazioni equivalenti; la sua calibrazione non è verificata.')}
                  </p>
                </div>
              ))}
              </fieldset>
              {initialGainsEditor}
              <fieldset hidden={!isHistoricalPortfolio} disabled={!isHistoricalPortfolio}>{trDisplay(formData.historicalPortfolio?.enabled && <SWRHistoricalPortfolio value={formData.historicalPortfolio} prepared={historicalState.prepared}
                initialCapital={formData.initialCapital} initialGainFraction={formData.initialGainFractionPct / 100} valuationDate={formData.valuationDate}
                useAssetGains={initialTaxMode === 'asset' && isSWRFlowTaxPortfolio({ historicalPortfolio: formData.historicalPortfolio })}
                error={errors.historicalPortfolio || (formData.historicalPortfolio.allocations.some(a => a.assetId || a.weight) ? historicalState.error : null)} onChange={handleHistoricalPortfolioChange} />)}</fieldset>
            </div><div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 space-y-4">
              {trDisplay(isHistoricalPortfolio ? <p className="text-sm text-slate-700" data-testid="swr-historical-inflation-notice">{trDisplay("Inflazione IPCA ricampionata insieme alle asset class. ")}{trDisplay(historicalState.prepared
                  ? `Riferimento medio applicato: ${formatPercentage(effectiveInflationPct / 100, { decimals: 2 })}.`
                  : 'Completa la composizione per vedere la media applicata.')}{trDisplay(" Regione e prudenza si scelgono nella sezione del portafoglio custom. ")}<SWRHelp topic="historicalInflation" />
              </p> : <>
              <FormField help="inflation" label={trDisplay("Inflazione media annua attesa di lungo periodo (%)")} error={errors.inflationRatePct}>
                <FormattedInput value={formData.inflationRatePct} onChange={(v) => handleChange('inflationRatePct', v || 0)} decimals={1} isPercent className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("es. 3%")} data-testid="swr-inflation" />
              </FormField>

              {trDisplay(isAdvancedMode && (
              <div className="flex items-start gap-2 rounded-md border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-950">
                <Landmark className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="font-semibold">{trDisplay("Inflazione stocastica")}</span>
                      <SWRHelp topic="stochasticInflation" />
                    </div>
                    <Switch
                      checked={isStochasticMacro}
                      onCheckedChange={(checked) => handleChange(
                        'macroMode',
                        checked ? SWR_MACRO_MODE_STOCHASTIC_EURO_V1 : SWR_MACRO_MODE_FIXED
                      )}
                      aria-label={trDisplay("Inflazione stocastica")}
                      data-testid="swr-macro-mode-toggle"
                    />
                  </div>
                  <p className="mt-1 text-indigo-800">
                    {trDisplay(isStochasticMacro
                      ? `Attiva: il ${formatInputDisplay(formData.inflationRatePct, { decimals: 1 })}% resta il riferimento centrale di lungo periodo.`
                      : (isPersonalizedStrategy || hasCashBuffer) ? 'Disattivata: inflazione costante; rendimento del buffer secondo il modello scelto.' : 'Disattivata: inflazione costante.')}
                  </p>
                </div>
              </div>
              ))}
              {trDisplay(errors.macroMode && <p className="text-sm text-red-500">{trDisplay(errors.macroMode)}</p>)}

              {trDisplay(isStochasticMacro && (
                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900">{trDisplay("Il modello storico genera l’inflazione usando anche una variabile ausiliaria sui depositi. La risposta del fondo monetario è un’ipotesi separata e modificabile, non una calibrazione della politica BCE.")}</div>
              ))}
              </>)}
            </div></section><fieldset hidden={!isAdvancedMode} disabled={!isAdvancedMode}><section aria-labelledby="swr-technical-section-heading" data-testid="swr-technical-section" className="swr-configuration-group space-y-4"><h2 id="swr-technical-section-heading" className="border-b border-border pb-3 text-lg font-semibold text-slate-800">{trDisplay('Costi, fiscalità e metodo')}</h2><details className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:p-4">
              <summary className="cursor-pointer text-sm font-semibold text-slate-800">{trDisplay("Costi e imposta patrimoniale · costi ")}{formatInputDisplay(formData.annualPortfolioCostRatePct, { decimals: 2 })}{trDisplay("% + imposta patrimoniale ")}{formatInputDisplay(formData.annualWealthTaxRatePct, { decimals: 2 })}%</summary>
              <p className="mt-2 text-xs text-slate-600">{trDisplay("I valori zero escludono questi costi. Inserisci solo spese non già incluse nel rendimento. L’imposta patrimoniale è una stima annuale distribuita sui mesi, non un calcolo fiscale per singolo strumento.")}</p>
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {trDisplay([['annualPortfolioCostRatePct', 'Costi annui del portafoglio (%)'], ['annualWealthTaxRatePct', 'Imposta patrimoniale annua (%)']].map(([field, label]) => (
                  <FormField help={field === 'annualPortfolioCostRatePct' ? 'portfolioCosts' : 'wealthTax'} key={field} label={trDisplay(label)} error={errors[field]}>
                    <FormattedInput value={formData[field]} onChange={v => handleChange(field, v)} decimals={2} isPercent data-testid={`swr-${field}`} className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" />
                  </FormField>
                )))}
              </div>
            </details>{trDisplay((
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:p-4 space-y-4">
                <div className="flex items-start gap-2">
                  <SlidersHorizontal className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-600" />
                  <div>
                    <Label className="text-sm font-semibold text-slate-800">{trDisplay("Fiscalità e metodo Monte Carlo")}</Label>
                    <p className="mt-1 text-xs text-slate-500">{trDisplay("Personalizza le assunzioni tecniche usate dall'analisi.")}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {trDisplay(!isSWRFlowTaxPortfolio({ historicalPortfolio: isHistoricalPortfolio ? formData.historicalPortfolio : null }) ? <FormField help="taxRate"
                    label={trDisplay("Aliquota fiscale sui rendimenti (%)")}
                    error={errors.taxRatePct}
                  >
                    <FormattedInput
                      value={formData.taxRatePct}
                      onChange={(v) => handleChange('taxRatePct', v || 0)}
                      decimals={2}
                      isPercent
                      className={isCustomPortfolio
                        ? "border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                        : "border-emerald-100 bg-emerald-50 text-emerald-900"}
                      placeholder={trDisplay("es. 26%")}
                      data-testid="swr-tax-rate"
                    />
                    {trDisplay(!isCustomPortfolio && selectedModelPortfolio && (
                      <p className="text-xs text-emerald-700">{trDisplay("Valore indicativo del preset: ")}{trDisplay(formatInputDisplay(selectedModelPortfolio.taxRatePct, { decimals: 2 }))}{trDisplay("%. Azioni al 26% e proxy fiscale AGGH per i bond; non cambia la calibrazione dei rendimenti. Puoi adattare l’aliquota.")}</p>
                    ))}
                  </FormField> : <p className="text-sm text-slate-700 sm:col-span-2">{trDisplay("Aliquote per strumento e regime amministrato italiano: il dettaglio fiscale si trova nella composizione del portafoglio custom. ")}<SWRHelp topic="customTax" /></p>)}


                </div>

                <div className="grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 sm:grid-cols-2">
                  <FormField help="simulations"
                    label={trDisplay("Numero simulazioni Monte Carlo")}
                    error={errors.numberOfSimulations}
                  >
                    <FormattedInput
                      value={formData.numberOfSimulations}
                      onChange={(v) => handleChange('numberOfSimulations', v === '' ? '' : Math.round(v))}
                      decimals={0}
                      className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      placeholder={trDisplay(`es. ${SWR_MAIN_DEFAULT_SIMULATIONS.toLocaleString(getLocale())}`)}
                      data-testid="swr-simulations"
                    />
                  </FormField>

                {trDisplay(!isHistoricalPortfolio && <FormField help="artificialLossCap" label={trDisplay("Perdita massima per singolo anno (%, opzionale)")} error={errors.maxSingleYearDrawdownPct}>
                  <FormattedInput value={formData.maxSingleYearDrawdownPct} onChange={(v) => handleChange('maxSingleYearDrawdownPct', v)} decimals={0} isPercent={formData.maxSingleYearDrawdownPct !== '' && formData.maxSingleYearDrawdownPct !== null} className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("Vuoto = nessun limite (es. 30 = max -30%)")} data-testid="swr-max-drawdown" />
                </FormField>)}
                </div>

                <FormField help="seed" label={trDisplay("Seed casuale (opzionale)")}>
                  <Input type="text" value={formData.randomSeed} onChange={(e) => handleChange('randomSeed', e.target.value)} className="border-slate-200 focus:border-blue-500 focus:ring-blue-500" placeholder={trDisplay("es. 12345 o qualsiasi testo")} data-testid="swr-seed" />
                </FormField>
              </div>
            ))}</section></fieldset>{!isAdvancedMode && <p className="text-xs text-muted-foreground" data-testid="swr-fixed-wealth-tax">{trDisplay('Imposta di bollo: 0,2% annuo. Costi aggiuntivi: 0%.')}</p>}

            <div className="swr-run-actions space-y-2" style={actionBounds ? { left: actionBounds.left, width: actionBounds.width, right: 'auto' } : undefined} data-testid="swr-floating-actions">
            <Button type="submit" disabled={isLoading} aria-busy={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-4 sm:py-6 text-sm sm:text-base transition-colors min-h-[48px]" data-testid="swr-run-simulation">
              {trDisplay(isLoading ? (
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="min-w-0 truncate">{trDisplay(progressPhase || 'Simulazione in corso')}... {trDisplay(progressValue)}%</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  <span>{trDisplay("Esegui simulazione")}</span>
                </div>
              ))}
            </Button>

            {trDisplay((isLoading || hasBackgroundWork) && onCancel && (
              <Button type="button" variant="outline" className="min-h-[44px] w-full" onClick={onCancel} data-testid="swr-cancel-calculations">{trDisplay("Interrompi calcoli")}</Button>
            ))}
            </div>
            <div className="h-28" aria-hidden="true" />
            {trDisplay(!isLoading && ['cancelled', 'error'].includes(progress?.status) && (
              <p role={progress.status === 'error' ? 'alert' : 'status'} className={`text-sm ${progress.status === 'error' ? 'text-red-700' : 'text-slate-600'}`}>
                {trDisplay(progress.phase)}
              </p>
            ))}
            {trDisplay(isLoading && (
              <div className="space-y-2">
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progressValue}%` }} />
                </div>
                <p className="text-sm text-slate-500 text-center">
                  {trDisplay(progressPhase || 'Simulazione in corso')}{trDisplay(": il calcolo avviene in background nel browser.")}</p>
              </div>
            ))}
          </form>
        </CardContent>
      </div>
    </Card>
  );
};

export default SWRScenarioForm;
